/*
 * Do NOT import components that use hooks in this file. This file is rendered with ReactDOMServer, and hooks are not supported.
 */
import {
  CARESEEKER_TYPE,
  CORONA_NOT_TESTED,
  CORONA_TESTED,
  HEALTH_INSURANCE_STATUS,
  LIVING_SITUATION_OTHER,
  PAYERS_MAPPING,
  PREDICAMENT_STATE_NOT_APPLICABLE,
  PREDICAMENT_STATE_YES,
  RELATIONSHIP_TO_PATIENT,
  SEARCH_TYPE_HOME_CARE,
  SEARCH_TYPE_HOSPITAL,
  SEARCH_TYPE_MEDICAL_SUPPLIES,
  SEARCH_TYPE_PSYCHIATRIC_CARE,
} from "core/consts";
import { getPayersLabels } from "core/consts/labels";
import { getName } from "core/model/accounts";
import { isCareSearch, isRehabSearch } from "core/model/auctions";
import { getDecryptedValue } from "core/model/encryption";
import {
  getAge,
  getCareLevelValue,
  getInsuranceStatusLabel,
  getInterval,
} from "core/model/patients";
import { getRequestOrigin } from "core/model/requests";
import { formatPhoneNumber } from "core/model/utils/contacts";
import { convertBirthDateIn, formatUnixDate } from "core/model/utils/dates";
import {
  formatLocation,
  locationValue,
} from "core/model/utils/location/formatters";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { buildLine, concatStrings } from "core/model/utils/strings";
import {
  AccessDrainages as AccessDrainagesType,
  Auction,
  AuctionRequest as AuctionRequestType,
  Careseeker,
  Financing,
  GetOntologyType,
  Patient,
  SearchType,
  SocialWorker,
} from "core/types";
import { Locale as DateFnsLocale } from "date-fns";
import { margin } from "ds_legacy/materials/metrics";
import {
  Body,
  Email,
  FONT_SIZE_14,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { patient } from "dsl/atoms/FaxPatient/TestConsts";
import { RenderMultiLine } from "dsl/atoms/RenderMultiLine";
import { solutionSortOrder } from "dsl/ecosystems/PatientCreatePage/PatientCreateForm";
import { getSelectedOntologies } from "dsl/ecosystems/PatientProfile";
import { getDirectRequestLabel } from "dsl/organisms/PatientProfileDetailsCareprovider/OverviewCard";
import { AdmissionsCareCardPrint } from "dsl/organisms/PatientProfileDetailsShared/AdmissionsCareCardPrint";
import { AdmissionsMainContactCardPrint } from "dsl/organisms/PatientProfileDetailsShared/AdmissionsMainContactCardPrint";
import { AdmissionsMedicalInformationCardPrint } from "dsl/organisms/PatientProfileDetailsShared/AdmissionsMedicalInformationCardPrint";
import { AdmissionsOtherInformationCardPrint } from "dsl/organisms/PatientProfileDetailsShared/AdmissionsOtherInformationPrint";
import { AdmissionsPatientCardPrint } from "dsl/organisms/PatientProfileDetailsShared/AdmissionsPatientCardPrint";
import { careneedsFilters } from "dsl/organisms/PatientProfileDetailsShared/CareNeedsCard";
import { generalInformationFilters } from "dsl/organisms/PatientProfileDetailsShared/InformationCard";
import {
  coronaSwitch,
  medicalFilters,
} from "dsl/organisms/PatientProfileDetailsShared/MedicalCard";
import {
  formatProductString,
  medicalSuppliesFilters,
} from "dsl/organisms/PatientProfileDetailsShared/MedicalSuppliesSearchCard";
import {
  BodyWrapper,
  BooleanField,
  OntologyValue,
  StringField,
  isInfectionsAndGermsPredicamentNegative,
} from "dsl/organisms/PatientProfileDetailsShared/shared";
import {
  activableInputCollectionIsNotEmpty,
  shouldHideStreetAndHouseNumber,
} from "dsl/organisms/PatientProfileDetailsShared/utils";
import { StaticGrantStatusField } from "dsl/static/StaticGrantStatusField";
import { CornerUpRightIcon } from "lucide-react";
import React, { ReactNode } from "react";
import { getPredicamentValue } from "react-forms-state";
import { Locale, Locale as TranslationsLocale } from "translations";
import Translations from "translations/types";
import { StaticChipInfo } from "./StaticChipInfo";
import { StaticIcdCodes } from "./StaticIcdCodes";
import { StaticIndependenceScaleResult } from "./StaticIndependenceScaleResult";
import { StaticShortTermCareGrant } from "./StaticShortTermCareGrant";
import { StaticTransportInfoCard } from "./StaticTransportInfoCard";
import { formatTimestamp } from "./utils";

const careneedsComponents = [
  {
    Component: Support,
    key: "support",
  },
  {
    Component: PsychiatricCare,
    key: "psychiatric_care",
  },
  {
    Component: NeedForAssistanceDescription,
    key: "need_for_assistance_description",
  },
  {
    Component: IntegrationAssistance,
    key: "integration_assistance",
  },
  {
    Component: PalliativeCare,
    key: "palliative_care",
  },
  {
    Component: Nutrition,
    key: "nutrition",
  },
  {
    Component: Incontinence,
    key: "incontinence",
  },
  {
    Component: Breathing,
    key: "breathing",
  },
  {
    Component: Dialysis,
    key: "Dialysis",
  },
  {
    Component: Wounds,
    key: "Wounds",
  },
  {
    Component: Medication,
    key: "medication",
  },
  {
    Component: Compression,
    key: "compression",
  },
  {
    Component: Injection,
    key: "injection",
  },
  {
    Component: AccessDrainages,
    key: "access_drainages",
  },
  {
    Component: ImportantInformation,
    key: "important_information",
  },
  {
    Component: BaseCare,
    key: "base_care",
  },
  {
    Component: MedicalCare,
    key: "basic_medical_care",
  },
  {
    Component: Companionship,
    key: "companionship",
  },
  {
    Component: HelpAtHome,
    key: "help_at_home",
  },
  {
    Component: Remarks,
    key: "remarks",
  },
  {
    Component: CareForChildrenAndTeenagers,
    key: "care_for_children_and_teenagers",
  },
  {
    Component: CareForYouth,
    key: "care_for_youth",
  },
] as const;

const medicalDiagnosisComponents = [
  {
    Component: IcdCodesForPrint,
    key: "icd_codes",
  },
  {
    Component: InfectionAndGerms,
    key: "infection_and_germs",
  },
  {
    Component: CoronaVirusTesting,
    key: "corona_testing",
  },
  {
    Component: DependencyDiagnosis,
    key: "dependencyDiagnosis",
  },
  {
    Component: Dysphagia,
    key: "dysphagia",
  },
  {
    Component: ChronicPains,
    key: "chronic_pains",
  },
  {
    Component: Allergies,
    key: "allergies",
  },
  {
    Component: Addictions,
    key: "addictions",
  },
  {
    Component: HospitalStay,
    key: "hospital_stay",
  },
  {
    Component: LimitedCapacity,
    key: "limited_capacity",
  },
  {
    Component: RehabilitationGoals,
    key: "rehabilitation_goals",
  },
  {
    Component: RehabilitationPotential,
    key: "rehabilitation_potential",
  },
] as const;

const baseMentalHealthComponents = [
  {
    Component: Vigilance,
    key: "vigilance",
  },
  {
    Component: PatientOrientation,
    key: "patient_orientation",
  },
  {
    Component: PatientMotivation,
    key: "patient_motivation",
  },
  {
    Component: PatientCommunication,
    key: "patient_communication",
  },
  {
    Component: Dementia,
    key: "dementia",
  },
  {
    Component: ProtectedAreaNeeded,
    key: "protected_area_needed",
  },
  {
    Component: RunAway,
    key: "run_away",
  },
  {
    Component: Nightwatch,
    key: "night_watch",
  },
  {
    Component: FacilityCharacteristics,
    key: "facility_characteristics",
  },
  {
    Component: MentalIssues,
    key: "mental_issues",
  },
] as const;

/** Psychiatric care search only: consciousness + orientation axes. */
const psychExpandedMentalHealthComponents = [
  {
    Component: Consciousness,
    key: "consciousness",
  },
  {
    Component: ConsciousnessDescription,
    key: "consciousness_description",
  },
  {
    Component: OrientationPerson,
    key: "orientation_person",
  },
  {
    Component: OrientationPersonDescription,
    key: "orientation_person_description",
  },
  {
    Component: OrientationPlace,
    key: "orientation_place",
  },
  {
    Component: OrientationPlaceDescription,
    key: "orientation_place_description",
  },
  {
    Component: OrientationSituation,
    key: "orientation_situation",
  },
  {
    Component: OrientationSituationDescription,
    key: "orientation_situation_description",
  },
  {
    Component: OrientationTime,
    key: "orientation_time",
  },
  {
    Component: OrientationTimeDescription,
    key: "orientation_time_description",
  },
] as const;

const psychSpecificMentalHealthComponents = [
  {
    Component: SelfHarm,
    key: "self_harm",
  },
  {
    Component: SelfHarmDescription,
    key: "self_harm_description",
  },
  {
    Component: Suicidality,
    key: "suicidality",
  },
  {
    Component: SuicidalityDescription,
    key: "suicidality_description",
  },
  {
    Component: Endangerment,
    key: "endangerment_of_others",
  },
  {
    Component: EndangermentDescription,
    key: "endangerment_of_others_description",
  },
  {
    Component: SubstanceUse,
    key: "substance_use",
  },
  {
    Component: SubstanceUseDescription,
    key: "substance_use_description",
  },
] as const;

const briefSociopsychiatricAssessmentComponents = [
  {
    Component: ContactBehaviour,
    key: "brief_sociopsychiatric_assessment_contact_behavior",
  },
  {
    Component: ContactBehaviourDescription,
    key: "brief_sociopsychiatric_assessment_contact_behavior_description",
  },
  {
    Component: DailyStructure,
    key: "brief_sociopsychiatric_assessment_daily_structure_participation",
  },
  {
    Component: DailyStructureDescription,
    key: "brief_sociopsychiatric_assessment_daily_structure_participation_description",
  },
  {
    Component: GafScore,
    key: "brief_sociopsychiatric_assessment_gaf_score",
  },
  {
    Component: MotivationPatientStatic,
    key: "brief_sociopsychiatric_assessment_motivation",
  },
  {
    Component: MotivationPatientDescriptionStatic,
    key: "brief_sociopsychiatric_assessment_motivation_description",
  },
] as const;

const physicalComponents = [
  {
    Component: PatientMobility,
    key: "patient_mobility",
  },
  {
    Component: VisualImpairment,
    key: "visual_impairment",
  },
  {
    Component: AuditiveImpairment,
    key: "auditive_impairment",
  },
  {
    Component: AssistiveEquipment,
    key: "assistive_equipment",
  },
  {
    Component: Orthosis,
    key: "orthosis",
  },
  {
    Component: Prothesis,
    key: "prothesis",
  },
] as const;

const generalInformationComponents = [
  {
    Component: RelevantProviderInformation,
    key: "relavant_information",
  },
  {
    Component: StaticPatientProfileGeneralInformation,
    key: "general_information",
  },
  {
    Component: Insurance,
    key: "insurance",
  },
  {
    Component: Carelevel,
    key: "carelevel",
  },
  {
    Component: EntitlementGroup,
    key: "benefit_entitlement_group",
  },
  {
    Component: Payout,
    key: "payout",
  },
  {
    Component: LivingSituation,
    key: "living_situation",
  },
  {
    Component: GeneralPractitioner,
    key: "general_practitioner",
  },
  {
    Component: Psychiatrist,
    key: "psychiatrist",
  },
  {
    Component: Communication,
    key: "communication",
  },
  {
    Component: Grants,
    key: "grants",
  },
  {
    Component: Payers,
    key: "payers",
  },
  {
    Component: Preferences,
    key: "preferences",
  },
] as const;

const medicalSuppliesSearchComponents = [
  {
    Component: Products,
    key: "products",
  },
  {
    Component: Prescription,
    key: "prescription",
  },
  {
    Component: DeliveryDetails,
    key: "delivery_details",
  },
] as const;

function BodyWithBreak({
  children,
  isFaxPrint,
  testId,
}: {
  children: ReactNode;
  isFaxPrint: boolean | null | undefined;
  testId: string;
}) {
  return (
    <>
      <Body data-testid={testId}>{children}</Body>
      {isFaxPrint ? <br /> : null}
    </>
  );
}

function EmptyablePrintCategory({
  children,
  isEmpty,
  isFaxPrint,
  testId,
  title,
  translations,
}: {
  children: React.ReactNode;
  isEmpty: boolean;
  isFaxPrint: boolean | null | undefined;
  testId: string;
  title: string;
  translations: Translations;
}) {
  return (
    <div>
      <Subheading bold>{title}</Subheading>
      {isEmpty ? (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId={testId}>
          {translations.auctionRequest.noAdditionalInformationProvided}
        </BodyWithBreak>
      ) : (
        children
      )}
    </div>
  );
}

function EmptiablePrintField({
  isFaxPrint,
  prefix,
  testId,
  value,
}: {
  isFaxPrint: boolean | null | undefined;
  prefix: string;
  testId: string;
  value: string | null | undefined;
}) {
  if (value == null) return null;

  return (
    <BodyWithBreak isFaxPrint={isFaxPrint} testId={testId}>
      {concatStrings(prefix, value, ": ")}
    </BodyWithBreak>
  );
}

function Diagnosis({
  auction,
  getOntology,
  isFaxPrint,
  isForClinic,
  locale,
  patient,
  request,
  translations,
}: {
  auction: Auction;
  isFaxPrint: boolean | null | undefined;
  isForClinic: boolean;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  patient: Patient;
  request?: AuctionRequestType;
  translations: Translations;
}) {
  const { isProviderSearchRequest } = getRequestOrigin(request);
  if (isProviderSearchRequest || !patient?.diagnosis) return null;

  const isPsychAssessment =
    auction.search_type === SEARCH_TYPE_PSYCHIATRIC_CARE;

  // Create filtered medicalFilters that exclude psych-specific fields for non-psychiatric care
  const filteredMedicalFilters = isPsychAssessment
    ? medicalFilters
    : Object.fromEntries(
        Object.entries(medicalFilters).filter(
          ([key]) =>
            ![
              "endangerment_of_others",
              "endangerment_of_others_description",
              "self_harm",
              "self_harm_description",
              "substance_use",
              "substance_use_description",
              "suicidality",
              "suicidality_description",
            ].includes(key),
        ),
      );

  const categories = medicalDiagnosisComponents.filter((c) =>
    filteredMedicalFilters[c.key].exists(auction, !!isForClinic),
  );

  if (!categories.length) return null;

  return (
    <>
      <Title bold>{translations.patient.medicalDiagnosis.stepperTitle}</Title>
      <MainDiagnosis
        isFaxPrint={isFaxPrint}
        patient={patient}
        translations={translations}
      />
      <SecondaryDiagnosis
        isFaxPrint={isFaxPrint}
        patient={patient}
        translations={translations}
      />
      <MorbidityStatus
        isActive={patient.diagnosis?.multimorbidity}
        isFaxPrint={isFaxPrint}
        translations={translations}
      />
      <MedicalHistory
        isFaxPrint={isFaxPrint}
        patient={patient}
        translations={translations}
      />
      <AdditionalExaminationsNecessary
        isFaxPrint={isFaxPrint}
        patient={patient}
        translations={translations}
      />
      {categories.map(({ Component, key }) => (
        <Component
          key={key}
          getOntology={getOntology}
          isFaxPrint={isFaxPrint}
          isForClinic={!!isForClinic}
          locale={locale}
          patient={patient}
          searchType={auction.search_type}
          translations={translations}
        />
      ))}
    </>
  );
}

function MentalHealth({
  auction,
  getOntology,
  isFaxPrint,
  isForClinic,
  translations,
}: {
  auction: Auction | null;
  isFaxPrint: boolean | null | undefined;
  isForClinic: boolean;
  getOntology: GetOntologyType;
  translations: Translations;
}) {
  if (!auction?.patient?.diagnosis) return null;

  const isPsychAssessment =
    auction.search_type === SEARCH_TYPE_PSYCHIATRIC_CARE;

  const mentalHealthComponents = isPsychAssessment
    ? [
        ...baseMentalHealthComponents,
        ...psychExpandedMentalHealthComponents,
        ...psychSpecificMentalHealthComponents,
      ]
    : baseMentalHealthComponents;

  const categories = mentalHealthComponents.filter((c) =>
    medicalFilters[c.key].exists(auction, isForClinic),
  );

  const bsaCategories = isPsychAssessment
    ? briefSociopsychiatricAssessmentComponents.filter((c) =>
        medicalFilters[c.key].exists(auction, isForClinic),
      )
    : [];

  if (!categories.length && !bsaCategories.length) return null;

  return (
    <>
      {categories.length > 0 && (
        <>
          <Title bold>{translations.patient.mentalHealth}</Title>
          {categories.map(({ Component, key }) => (
            <Component
              key={key}
              getOntology={getOntology}
              isFaxPrint={isFaxPrint}
              patient={auction.patient}
              translations={translations}
            />
          ))}
        </>
      )}
      {bsaCategories.length > 0 && (
        <>
          <Title bold>
            {translations.patient.psychEvaluation.sectionTitle}
          </Title>
          {bsaCategories.map(({ Component, key }) => (
            <Component
              key={key}
              getOntology={getOntology}
              isFaxPrint={isFaxPrint}
              patient={auction.patient}
              translations={translations}
            />
          ))}
        </>
      )}
    </>
  );
}

function PhysicalHealth({
  auction,
  getOntology,
  isFaxPrint,
  isForClinic,
  translations,
}: {
  auction: Auction | null;
  isFaxPrint: boolean | null | undefined;
  isForClinic: boolean;
  getOntology: GetOntologyType;
  translations: Translations;
}) {
  if (!auction?.patient?.diagnosis) return null;

  const isPsychAssessment =
    auction.search_type === SEARCH_TYPE_PSYCHIATRIC_CARE;

  // Create filtered medicalFilters that exclude psych-specific fields for non-psychiatric care
  const filteredMedicalFilters = isPsychAssessment
    ? medicalFilters
    : Object.fromEntries(
        Object.entries(medicalFilters).filter(
          ([key]) =>
            ![
              "endangerment_of_others",
              "endangerment_of_others_description",
              "self_harm",
              "self_harm_description",
              "substance_use",
              "substance_use_description",
              "suicidality",
              "suicidality_description",
            ].includes(key),
        ),
      );

  const categories = physicalComponents.filter((c) =>
    filteredMedicalFilters[c.key].exists(auction, isForClinic),
  );

  if (!categories.length) return null;

  return (
    <>
      <Title>{translations.patient.physicalHealth}</Title>
      {categories.map(({ Component, key }) => (
        <Component
          key={key}
          getOntology={getOntology}
          isFaxPrint={isFaxPrint}
          patient={auction.patient}
          translations={translations}
        />
      ))}
    </>
  );
}

function GeneralInformationContainer({
  auction,
  currentLocale,
  getOntology,
  hideStreetAndHouseNumber,
  isFaxPrint,
  isForClinic,
  locale,
  patient,
  request,
  translations,
}: {
  auction: Auction;
  currentLocale: DateFnsLocale;
  isFaxPrint: boolean | null | undefined;
  isForClinic: boolean;
  getOntology: GetOntologyType;
  hideStreetAndHouseNumber: boolean;
  locale: TranslationsLocale;
  patient: Patient;
  request: AuctionRequestType | null | undefined;
  translations: Translations;
}) {
  const categories = generalInformationComponents.filter((c) =>
    generalInformationFilters[c.key].exists(auction, isForClinic),
  );

  if (!categories.length) return null;

  return (
    <>
      <Title bold>{translations.patient.patientInformation.stepperTitle}</Title>
      {categories.map(({ Component, key }) => (
        <Component
          key={key}
          auction={auction}
          currentLocale={currentLocale}
          getOntology={getOntology}
          hideStreetAndHouseNumber={hideStreetAndHouseNumber}
          isFaxPrint={isFaxPrint}
          isForClinic={!!isForClinic}
          locale={locale}
          patient={patient}
          request={request ?? undefined}
          translations={translations}
        />
      ))}
    </>
  );
}

function MedicalSuppliesContainer({
  auction,
  getOntology,
  isFaxPrint,
  isForClinic,
  translations,
}: {
  auction: Auction;
  isFaxPrint: boolean | undefined | null;
  isForClinic: boolean;
  getOntology: GetOntologyType;
  translations: Translations;
}) {
  if (
    !(
      [SEARCH_TYPE_HOME_CARE, SEARCH_TYPE_MEDICAL_SUPPLIES] as SearchType[]
    ).includes(auction.search_type)
  ) {
    return null;
  }

  const categories = medicalSuppliesSearchComponents.filter((c) =>
    medicalSuppliesFilters[c.key].exists(auction, isForClinic),
  );

  if (!categories.length) return null;

  return (
    <>
      <Title bold>
        {translations.assessments.medicalSupplies.medicalSupplies}
      </Title>
      {categories.map((c) =>
        React.createElement(c.Component, {
          auction,
          getOntology,
          isFaxPrint,
          key: c.key,
          translations,
        }),
      )}
    </>
  );
}

function CareNeeds({
  auction,
  isFaxPrint,
  isForClinic,
  translations,
}: {
  auction: Auction;
  isFaxPrint: boolean | null | undefined;
  isForClinic: boolean;
  translations: Translations;
}) {
  if (!auction?.patient?.care_needs) return null;

  const categories = careneedsComponents.filter((c) =>
    careneedsFilters[c.key].exists(auction, isForClinic),
  );

  if (!categories.length) return null;

  return (
    <>
      <Title bold>{translations.patient.careNeeds.stepperTitle}</Title>
      {categories.map((c) =>
        React.createElement(c.Component, {
          isFaxPrint,
          key: c.key,
          patient: auction.patient,
          translations,
        }),
      )}
    </>
  );
}

function BasicCategory({
  isColumn,
  isFaxPrint,
  isMultiline = false,
  testId,
  text,
  title,
}: {
  isColumn?: boolean;
  isFaxPrint: boolean | null | undefined;
  isMultiline?: boolean;
  testId: string;
  text: string;
  title: string;
}) {
  return (
    <div
      style={{
        alignItems: "baseline",
        display: "flex",
        flexDirection: isColumn ? "column" : undefined,
      }}
    >
      <Subheading bold>{title}</Subheading>
      <BodyWithBreak isFaxPrint={isFaxPrint} testId={testId}>
        {isMultiline ? <RenderMultiLine value={text} /> : text}
      </BodyWithBreak>
    </div>
  );
}

function HospitalContactInformation({
  careseeker,
  getOntology,
  socialWorker,
  translations,
}: {
  careseeker: Careseeker | undefined;
  getOntology: GetOntologyType;
  socialWorker: SocialWorker | undefined;
  translations: Translations;
}) {
  if (
    !socialWorker ||
    !careseeker ||
    careseeker.type === CARESEEKER_TYPE.PROVIDER_SEARCH ||
    careseeker.type === CARESEEKER_TYPE.ADMISSIONS
  ) {
    return null;
  }

  return (
    <>
      <Subheading bold>
        {translations.careproviderProfile.clinicContactInformation({
          careseekerName: careseeker.name || "",
        })}
      </Subheading>
      <StringField
        testId="careseeker_name"
        value={getName(socialWorker, getOntology)}
      />
      <StringField
        prefix={translations.people.tel}
        testId="social_worker_phone"
        value={formatPhoneNumber(socialWorker.phone)}
      />
      <StringField
        prefix={translations.people.mobile}
        testId="social_worker_mobile_phone"
        value={formatPhoneNumber(socialWorker.mobile_phone)}
      />
      {socialWorker.email && (
        <div>
          <Email href={`mailto:${socialWorker.email}`}>
            {translations.people.mailShort}&nbsp;{socialWorker.email}
          </Email>
        </div>
      )}
    </>
  );
}

function RelevantProviderInformation({
  auction,
  currentLocale,
  getOntology,
  isFaxPrint,
  isForClinic,
  locale,
  patient,
  request,
  translations,
}: {
  auction: Auction;
  currentLocale: DateFnsLocale;
  isFaxPrint: boolean | null | undefined;
  isForClinic: boolean;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  request: AuctionRequestType | undefined;
  translations: Translations;
}) {
  if (!patient) return null;

  const {
    careseeker,
    diagnosis,
    external_id,
    profile,
    social_worker,
    user_id,
  } = patient;

  const typeOfTransfer = getSelectedOntologies(
    auction.solutions,
    ONTOLOGIES.SOLUTION,
    getOntology,
    solutionSortOrder,
  );

  const specialization = getSelectedOntologies(
    auction.specializations,
    ONTOLOGIES.SPECIALIZATIONS,
    getOntology,
  );

  const dischargeDate = formatTimestamp(
    auction.patient.profile?.discharge_date,
    currentLocale,
  );
  const startDate = formatTimestamp(auction.start_date, currentLocale);

  const directRequestLabel = getDirectRequestLabel({
    requestOrigin: request?.request_origin,
    state: request?.careprovider?.provider_search_info?.state,
    translations,
  });

  return (
    <div>
      {user_id && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="user_id"
        >{`${translations.patient.patientId}: ${user_id}`}</BodyWithBreak>
      )}
      {directRequestLabel && (
        <div
          className="m-4 flex items-center"
          data-testid="direct-request-header"
        >
          <CornerUpRightIcon size={14} />
          <Body
            fontSize={FONT_SIZE_14}
            fontWeight={FONT_WEIGHT_MEDIUM}
            margin={margin(0, 1)}
          >
            {directRequestLabel}
          </Body>
        </div>
      )}
      {isForClinic && (
        <>
          {careseeker && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="careseeker_name"
            >{`${translations.auctionRequest.hospitalName}: ${careseeker.name}`}</BodyWithBreak>
          )}
          {!!startDate && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="start_date"
            >{`${translations.patient.expectedStartDate}: ${startDate}`}</BodyWithBreak>
          )}
          {isRehabSearch(auction?.search_type) && !!dischargeDate && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="discharge_date"
            >{`${translations.patient.expectedDischargeDate}: ${dischargeDate}`}</BodyWithBreak>
          )}
          {!!typeOfTransfer && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="type_of_transfer"
            >{`${translations.auctionRequest.typeOfTransfer}: ${typeOfTransfer}`}</BodyWithBreak>
          )}
          {!!specialization && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="specialization"
            >{`${translations.ontologies.specializations.key}: ${specialization}`}</BodyWithBreak>
          )}
          <br />
        </>
      )}
      {isForClinic && external_id != null && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="external_id">
          {`${translations.patient.externalId}: ${external_id}`}
        </BodyWithBreak>
      )}
      {profile?.first_name?.decrypted && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="first_name"
        >{`${translations.patient.firstName}: ${profile.first_name.decrypted}`}</BodyWithBreak>
      )}
      {profile?.last_name?.decrypted && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="last_name"
        >{`${translations.patient.lastName}: ${profile.last_name.decrypted}`}</BodyWithBreak>
      )}
      {request?.solutions?.length && (
        <div>
          <StaticChipInfo
            auction={auction}
            auctionRequest={request}
            getOntology={getOntology}
            locale={locale}
            translations={translations}
          />
        </div>
      )}
      {!isForClinic && !isFaxPrint && (
        <HospitalContactInformation
          careseeker={careseeker}
          getOntology={getOntology}
          socialWorker={social_worker}
          translations={translations}
        />
      )}
      {!isForClinic && patient.station_full?.name && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="station_full_name"
        >{`${translations.patient.station}: ${patient.station_full.name}`}</BodyWithBreak>
      )}
      {!isForClinic &&
        diagnosis?.hospital_stay?.doctor_in_charge_in_hospital && (
          <BodyWithBreak
            isFaxPrint={isFaxPrint}
            testId="doctor_in_charge_in_hospital"
          >{`${translations.patient.doctorInCharge}: ${diagnosis.hospital_stay.doctor_in_charge_in_hospital}`}</BodyWithBreak>
        )}
      {!isForClinic &&
        !isCareSearch(auction?.search_type) &&
        diagnosis?.hospital_stay?.doctor_in_charge_in_hospital_phone && (
          <BodyWithBreak
            isFaxPrint={isFaxPrint}
            testId="doctor_in_charge_in_hospital_phone"
          >{`${translations.people.tel}: ${diagnosis.hospital_stay.doctor_in_charge_in_hospital_phone}`}</BodyWithBreak>
        )}
    </div>
  );
}

function StaticPatientProfileGeneralInformation({
  auction,
  getOntology,
  hideStreetAndHouseNumber,
  isFaxPrint,
  locale,
  patient,
  translations,
}: {
  auction: Auction;
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  hideStreetAndHouseNumber: boolean;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  if (
    !patient?.profile ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS
  )
    return null;

  const { profile } = patient;
  const search_type = auction?.search_type;
  const location = auction?.profile?.search_location;
  const formattedAddressInfo = location
    ? formatLocation({
        hideStreetAndHouseNumber,
        location,
        showFloor: !!location?.floor,
        translations,
      })
    : null;
  const isProviderSearchPatient =
    patient?.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH;

  const weight =
    getDecryptedValue(profile?.weight) ||
    getInterval(
      patient,
      auction.search_type,
      isProviderSearchPatient,
      "weight_interval",
      translations,
    );
  const height =
    getDecryptedValue(profile?.height) ||
    getInterval(
      patient,
      auction.search_type,
      isProviderSearchPatient,
      "height_interval",
      translations,
    );

  return (
    <div>
      <Subheading bold>
        {translations.patient.patientInformation.generalInformation}
      </Subheading>
      {profile.gender && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="gender">{`${
          translations.patient.gender
        }: ${getOntology({
          key: profile.gender,
          type: "gender",
        })}`}</BodyWithBreak>
      )}
      {profile.birth_date?.decrypted ? (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="birth_date">{`${
          translations.patient.dateOfBirth
        }: ${convertBirthDateIn(profile.birth_date, {
          locale,
        })}`}</BodyWithBreak>
      ) : (
        profile.age_interval && (
          <BodyWithBreak isFaxPrint={isFaxPrint} testId="age_interval">{`${
            translations.patient.age
          }: ${
            getAge(profile.age_interval, search_type) || ""
          }`}</BodyWithBreak>
        )
      )}
      {weight && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="weight"
        >{`${translations.patient.formGeneration.weight}: ${weight}kg`}</BodyWithBreak>
      )}
      {height && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="height"
        >{`${translations.patient.formGeneration.height}: ${height}cm`}</BodyWithBreak>
      )}
      {formattedAddressInfo && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="address"
        >{`${translations.address.address}: ${formattedAddressInfo}`}</BodyWithBreak>
      )}
    </div>
  );
}

function Insurance({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  const financing = patient?.profile?.financing;
  const status = financing?.insurance_status;
  const isInsured = status === HEALTH_INSURANCE_STATUS.INSURED;
  const isUnclearOrSpecial =
    status === HEALTH_INSURANCE_STATUS.UNCLEAR_OR_SPECIAL;
  const additionalInfo = financing?.insurance_additional_information;

  const hasContent = isInsured
    ? !!financing?.insurance?.name || !!financing?.insurance_number?.decrypted
    : status != null || !!additionalInfo;

  if (
    !patient ||
    !hasContent ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  ) {
    return null;
  }

  return (
    <div>
      <Subheading bold>
        {translations.patient.patientInformation.insurance}
      </Subheading>
      {isInsured ? (
        <>
          {financing?.insurance?.name && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="financing_insurance_name"
            >{`${translations.patient.insuranceProvider}: ${financing.insurance.name}`}</BodyWithBreak>
          )}
          {financing?.insurance_number?.decrypted && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="financing_insurance_number"
            >{`${translations.patient.insuranceNumber}: ${financing.insurance_number.decrypted}`}</BodyWithBreak>
          )}
        </>
      ) : (
        <>
          {getInsuranceStatusLabel(status, translations) && (
            <BodyWithBreak isFaxPrint={isFaxPrint} testId="insurance_status">
              {`${translations.patient.insuranceStatus.label}: ${getInsuranceStatusLabel(status, translations)}`}
            </BodyWithBreak>
          )}
          {isUnclearOrSpecial && additionalInfo && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="insurance_additional_information"
            >
              {`${translations.patient.insuranceStatus.additionalInformationLabel}: ${additionalInfo}`}
            </BodyWithBreak>
          )}
        </>
      )}
    </div>
  );
}

function Carelevel({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  if (
    !patient?.profile?.financing?.carelevel ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  ) {
    return null;
  }

  const { carelevel, public_care_insurance_status } = patient.profile.financing;

  return (
    <div>
      <Subheading bold>{translations.patient.carelevel}</Subheading>
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="carelevel">
        {getCareLevelValue(carelevel, translations).label}
      </BodyWithBreak>

      {(carelevel?.higher_level || public_care_insurance_status) && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="higher_level">
          {translations.patient.hasAppliedforHigherCarelevel}
        </BodyWithBreak>
      )}
      {carelevel?.expedited_request && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="expedited_request">
          {translations.patient.expeditedRequest}
        </BodyWithBreak>
      )}
      {carelevel?.has_applied && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="has_applied">
          {translations.patient.hasAppliedforCarelevel}
        </BodyWithBreak>
      )}
    </div>
  );
}

function EntitlementGroup({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  const group = patient?.profile?.financing?.benefit_entitlement_group;
  if (
    group?.available == null ||
    patient?.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient?.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  ) {
    return null;
  }

  return (
    <div>
      <Subheading bold>
        {translations.patient.patientInformation.entitlementGroupLabel}
      </Subheading>
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="benefit_entitlement_group_available"
      >
        {group.available
          ? translations.patient.patientInformation.entitlementExisting
          : translations.patient.patientInformation.entitlementNotExisting}
      </BodyWithBreak>
      {group.available && group.description ? (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="benefit_entitlement_group_description"
        >
          {`${translations.patient.patientInformation.entitlementDescription}: ${group.description}`}
        </BodyWithBreak>
      ) : null}
    </div>
  );
}

function Payout({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  if (
    !patient?.profile?.financing?.payout ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  )
    return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="payout"
      text={getOntology({
        key: patient.profile.financing.payout,
        type: ONTOLOGIES.GRANTS_TYPE,
      })}
      title={translations.patient.patientInformation.payout}
    />
  );
}

function LivingSituation({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  if (
    !patient?.profile ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  )
    return null;

  const {
    lift_available_description,
    lift_available_state,
    living_situation,
    living_situation_other,
    unsafe_current_domestic_situation,
  } = patient.profile;

  if (
    !living_situation &&
    !lift_available_state &&
    !lift_available_description &&
    !unsafe_current_domestic_situation
  ) {
    return null;
  }

  const livingSituation = getOntology({
    key: living_situation as number,
    type: ONTOLOGIES.LIVING_SITUATION,
  });
  const other = living_situation === LIVING_SITUATION_OTHER;

  return (
    <div>
      <Subheading bold>
        {translations.patient.patientInformation.livingSituation}
      </Subheading>
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId={other ? "living_situation_other" : "living_situation"}
      >
        {`${translations.patient.patientInformation.patientIsLiving}: ${
          other ? living_situation_other || "" : livingSituation
        }`}
      </BodyWithBreak>
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="lift_available_state">
        {getPredicamentValue({
          label: translations.patient.patientInformation.liftAvailable,
          translations,
          value: lift_available_state,
        })}
      </BodyWithBreak>
      {lift_available_description && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="lift_available_description"
        >
          {lift_available_description}
        </BodyWithBreak>
      )}
      {unsafe_current_domestic_situation && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="unsafe_current_domestic_situation"
        >
          {`${translations.patient.patientInformation.unsafeCurrentDomesticSituation}: ${unsafe_current_domestic_situation}`}
        </BodyWithBreak>
      )}
    </div>
  );
}

function GeneralPractitioner({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  const gp = patient?.profile?.general_practitioner;

  if (
    !gp ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  ) {
    return null;
  }

  const hasLegacyFields = !!(gp.name || gp.contact_details);

  const fullName = buildLine(
    gp.honorific_prefix?.decrypted,
    gp.honorific_suffix?.decrypted,
    gp.first_name?.decrypted,
    gp.last_name?.decrypted,
  );

  const zipCodeCity = buildLine(gp.zipcode?.decrypted, gp.city?.decrypted);

  const streetAddress = buildLine(
    gp.street?.decrypted,
    gp.house_number?.decrypted,
  );

  const fullAddress = buildLine(
    zipCodeCity ? `${zipCodeCity},` : "",
    streetAddress,
  );

  return (
    <div data-testid="general_practitioner">
      <Subheading bold>
        {translations.patient.patientInformation.generalPractitionerTitle}
      </Subheading>

      {hasLegacyFields ? (
        <>
          {gp.name && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="general_practitioner_name"
            >
              {gp.name}
            </BodyWithBreak>
          )}
          {gp.contact_details && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="general_practitioner_contact_details"
            >
              {gp.contact_details}
            </BodyWithBreak>
          )}
        </>
      ) : (
        <>
          {fullName && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="general_practitioner_full_name"
            >
              {`${translations.patient.patientInformation.generalPractitionerNamePlaceholder}${translations.general.colon} ${fullName}`}
            </BodyWithBreak>
          )}
          {gp.job_title?.decrypted && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="general_practitioner_job_title"
            >
              {`${translations.patient.patientInformation.generalPractitionerJobTitle}${translations.general.colon} ${gp.job_title.decrypted}`}
            </BodyWithBreak>
          )}
          {fullAddress && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="general_practitioner_address"
            >
              {`${translations.address.address}${translations.general.colon} ${fullAddress}`}
            </BodyWithBreak>
          )}
          {gp.phone?.decrypted && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="general_practitioner_phone"
            >
              {`${translations.contactInformation.phoneNumber}${translations.general.colon} ${gp.phone.decrypted}`}
            </BodyWithBreak>
          )}
        </>
      )}
    </div>
  );
}

function Psychiatrist({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  const psychiatrist = patient?.profile?.psychiatrist;

  if (
    !psychiatrist ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  ) {
    return null;
  }

  const fullName = buildLine(
    psychiatrist.honorific_prefix?.decrypted,
    psychiatrist.honorific_suffix?.decrypted,
    psychiatrist.first_name?.decrypted,
    psychiatrist.last_name?.decrypted,
  );

  const zipCodeCity = buildLine(
    psychiatrist.zipcode?.decrypted,
    psychiatrist.city?.decrypted,
  );

  const streetAddress = buildLine(
    psychiatrist.street?.decrypted,
    psychiatrist.house_number?.decrypted,
  );

  const fullAddress = buildLine(
    zipCodeCity ? `${zipCodeCity},` : "",
    streetAddress,
  );

  return (
    <div>
      <Subheading bold>
        {translations.patient.patientInformation.psychiatrist.title}
      </Subheading>

      {fullName && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="psychiatrist_full_name">
          {`${translations.patient.patientInformation.psychiatrist.firstName}${translations.general.colon} ${fullName}`}
        </BodyWithBreak>
      )}
      {psychiatrist.job_title?.decrypted && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="psychiatrist_job_title">
          {`${translations.patient.patientInformation.psychiatrist.jobTitle}${translations.general.colon} ${psychiatrist.job_title.decrypted}`}
        </BodyWithBreak>
      )}
      {fullAddress && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="psychiatrist_address">
          {`${translations.address.address}${translations.general.colon} ${fullAddress}`}
        </BodyWithBreak>
      )}
      {psychiatrist.phone?.decrypted && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="psychiatrist_phone">
          {`${translations.contactInformation.phoneNumber}${translations.general.colon} ${psychiatrist.phone.decrypted}`}
        </BodyWithBreak>
      )}
    </div>
  );
}

function Communication({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  getOntology: GetOntologyType;
  translations: Translations;
}) {
  if (
    !patient?.profile?.communication ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  )
    return null;

  const { communication } = patient.profile;

  return (
    <div>
      <Subheading bold>
        {translations.patient.patientInformation.decisionsLanguages}
      </Subheading>

      {communication.patient_is_contact && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="patient_is_contact">
          {communication.patient_is_contact_description?.decrypted
            ? `${translations.patient.patientInformation.contactIsPatient}: ${communication.patient_is_contact_description.decrypted}`
            : translations.patient.patientInformation.contactIsPatient}
        </BodyWithBreak>
      )}
      {communication.relatives_available && (
        <>
          {communication.relatives_description?.decrypted ? (
            <BodyWithBreak isFaxPrint={isFaxPrint} testId="relatives_available">
              {`${translations.patient.patientInformation.relativesAvailable}${
                communication.relatives_description?.decrypted
                  ? `${translations.general.colon} ${communication.relatives_description?.decrypted}`
                  : ""
              }`}
            </BodyWithBreak>
          ) : (
            communication?.relatives?.length && (
              <>
                <BodyWithBreak
                  isFaxPrint={isFaxPrint}
                  testId="relatives_available"
                >
                  {translations.patient.patientInformation.relativesAvailable}
                </BodyWithBreak>
                {communication.relatives.map((relative, index) => {
                  const relativeNumber = index + 1;

                  const zipCodeCity = buildLine(
                    relative.zipcode?.decrypted,
                    relative.city?.decrypted,
                  );

                  const streetAddress = buildLine(
                    relative.street?.decrypted,
                    relative.house_number?.decrypted,
                  );

                  const fullAddress = buildLine(
                    zipCodeCity ? `${zipCodeCity},` : "",
                    streetAddress,
                  );

                  return (
                    <div key={relativeNumber} className="mb-4">
                      <Subheading bold>
                        {`${translations.patient.patientInformation.relative} ${relativeNumber}${translations.general.colon}`}
                      </Subheading>
                      {relative?.first_name?.decrypted && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`relative_first_name_${relativeNumber}`}
                        >
                          {translations.patient.firstName}
                          {translations.general.colon}{" "}
                          {relative?.first_name?.decrypted}
                        </BodyWithBreak>
                      )}
                      {relative?.last_name?.decrypted && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`relative_last_name_${relativeNumber}`}
                        >
                          {translations.patient.lastName}
                          {translations.general.colon}{" "}
                          {relative?.last_name?.decrypted}
                        </BodyWithBreak>
                      )}
                      {relative?.phone?.decrypted && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`relative_phone_${relativeNumber}`}
                        >
                          {translations.contactInformation.phoneNumber}
                          {translations.general.colon}{" "}
                          {relative?.phone?.decrypted}
                        </BodyWithBreak>
                      )}
                      {relative?.relationship?.decrypted &&
                        (parseInt(relative?.relationship?.decrypted) ===
                          RELATIONSHIP_TO_PATIENT.OTHER &&
                        relative.relationship_other?.decrypted ? (
                          <BodyWithBreak
                            isFaxPrint={isFaxPrint}
                            testId={`relative_relationship_other_${relativeNumber}`}
                          >
                            {translations.ontologies.relationship.key}
                            {translations.general.colon}{" "}
                            {relative.relationship_other.decrypted}
                          </BodyWithBreak>
                        ) : (
                          <BodyWithBreak
                            isFaxPrint={isFaxPrint}
                            testId={`relative_relationship_${relativeNumber}`}
                          >
                            {translations.ontologies.relationship.key}
                            {translations.general.colon}{" "}
                            {getOntology({
                              key: parseInt(relative.relationship.decrypted),
                              type: ONTOLOGIES.RELATIONSHIP_TO_PATIENT,
                            })}
                          </BodyWithBreak>
                        ))}
                      {fullAddress && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`relative_address_${relativeNumber}`}
                        >
                          {translations.address.address}
                          {translations.general.colon} {fullAddress}
                        </BodyWithBreak>
                      )}
                    </div>
                  );
                })}
              </>
            )
          )}
        </>
      )}
      {communication.has_guardian && (
        <>
          {communication.guardian_contact_information?.decrypted ? (
            <BodyWithBreak isFaxPrint={isFaxPrint} testId="has_guardian">
              {`${translations.patient.patientInformation.hasGuardian}
            ${
              communication.guardian_contact_information?.decrypted
                ? `${translations.general.colon} ${communication.guardian_contact_information?.decrypted}`
                : ""
            }`}
            </BodyWithBreak>
          ) : (
            communication?.guardians?.length && (
              <>
                <BodyWithBreak isFaxPrint={isFaxPrint} testId="has_guardian">
                  {translations.patient.patientInformation.hasGuardian}
                </BodyWithBreak>
                {communication.guardians.map((guardian, index) => {
                  const guardianNumber = index + 1;

                  const zipCodeCity = buildLine(
                    guardian.zipcode?.decrypted,
                    guardian.city?.decrypted,
                  );

                  const streetAddress = buildLine(
                    guardian.street?.decrypted,
                    guardian.house_number?.decrypted,
                  );

                  const fullAddress = [zipCodeCity, streetAddress]
                    .filter(Boolean)
                    .join(", ");

                  return (
                    <div key={guardianNumber} className="mb-4">
                      <Subheading bold>
                        {`${translations.patient.formGeneration.guardianIsCustodian} ${guardianNumber}${translations.general.colon}`}
                      </Subheading>
                      {guardian?.first_name?.decrypted && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`guardian_first_name_${guardianNumber}`}
                        >
                          {translations.patient.firstName}
                          {translations.general.colon}{" "}
                          {guardian?.first_name?.decrypted}
                        </BodyWithBreak>
                      )}
                      {guardian?.last_name?.decrypted && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`guardian_last_name_${guardianNumber}`}
                        >
                          {translations.patient.lastName}
                          {translations.general.colon}{" "}
                          {guardian?.last_name?.decrypted}
                        </BodyWithBreak>
                      )}
                      {guardian?.phone?.decrypted && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`guardian_phone_${guardianNumber}`}
                        >
                          {translations.contactInformation.phoneNumber}
                          {translations.general.colon}{" "}
                          {guardian?.phone?.decrypted}
                        </BodyWithBreak>
                      )}
                      {guardian?.responsibilities?.decrypted && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`guardian_responsibilities_${guardianNumber}`}
                        >
                          {translations.people.responsibilitiesLabel}
                          {translations.general.colon}{" "}
                          {guardian?.responsibilities?.decrypted}
                        </BodyWithBreak>
                      )}
                      {guardian?.type && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`guardian_type_${guardianNumber}`}
                        >
                          {translations.report.solution}
                          {translations.general.colon}{" "}
                          {getOntology({
                            key: guardian.type,
                            type: ONTOLOGIES.GUARDIAN_TYPE,
                          })}
                        </BodyWithBreak>
                      )}
                      {fullAddress && (
                        <BodyWithBreak
                          isFaxPrint={isFaxPrint}
                          testId={`guardian_address_${guardianNumber}`}
                        >
                          {translations.address.address}
                          {translations.general.colon} {fullAddress}
                        </BodyWithBreak>
                      )}
                    </div>
                  );
                })}
              </>
            )
          )}
          {communication.guardian_requested && (
            <BodyWithBreak isFaxPrint={isFaxPrint} testId="guardian_requested">
              {translations.patient.patientInformation.guardianRequested}
            </BodyWithBreak>
          )}
        </>
      )}
      {communication.patient_cannot_communicate_in_german && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="patient_cannot_communicate_in_german"
        >
          {`${
            translations.patient.patientInformation
              .patientCannotCommunicateInGerman.label
          }: ${communication.patient_language || ""}`}
        </BodyWithBreak>
      )}
      {communication.patient_uses_sign_language && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="patient_uses_sign_language"
        >
          {`${translations.patient.patientInformation.patientUsesSignInLanguage.label}: ${communication.patient_uses_sign_language}`}
        </BodyWithBreak>
      )}
    </div>
  );
}

function Payers({
  auction,
  isFaxPrint,
  translations,
}: {
  auction: Auction;
  isFaxPrint: boolean | null | undefined;
  locale: TranslationsLocale;
  translations: Translations;
}) {
  if (
    !auction.payers ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  )
    return null;

  const { payers } = auction;

  const labels = getPayersLabels(translations);

  const selectedField =
    payers.selected_payment_method &&
    PAYERS_MAPPING[payers.selected_payment_method];

  return (
    <div>
      <Subheading bold>
        {translations.patient.patientInformation.payers}
      </Subheading>
      {payers.selected_payment_method && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="selected_payment_method">
          {`${labels.selected_payment_method}: ${
            labels[selectedField as keyof typeof labels]
          }`}
        </BodyWithBreak>
      )}
      {payers.insurance?.name && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="payers_insurance_name">
          {`${translations.patient.patientInformation.payersInsurance}: ${payers.insurance.name}`}
        </BodyWithBreak>
      )}
      {payers.civil_servants_aid && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="civil_servants_aid">
          {`${translations.patient.patientInformation.civilServantsAid}: ${payers.civil_servants_aid}`}
        </BodyWithBreak>
      )}
      {payers.public_accident_insurance && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="public_accident_insurance"
        >
          {`${translations.patient.patientInformation.publicAccidentInsurance}: ${payers.public_accident_insurance}`}
        </BodyWithBreak>
      )}
      {payers.public_pension?.name && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="public_pension_name">
          {`${translations.patient.patientInformation.publicPension}: ${payers.public_pension.name}`}
        </BodyWithBreak>
      )}
      {payers.self_payer && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="self_payer">
          {`${translations.patient.patientInformation.selfPayer}: ${payers.self_payer}`}
        </BodyWithBreak>
      )}
      {payers.other_payer && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="payers_other_payer">
          {`${translations.patient.patientInformation.payersOther}: ${payers.other_payer}`}
        </BodyWithBreak>
      )}
      {payers.further_information && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="payers_further_information"
        >
          {`${translations.patient.patientInformation.payersMoreInformations}: ${payers.further_information}`}
        </BodyWithBreak>
      )}
      {payers.supplementary_insurance_state && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="supplementary_insurance_state"
        >
          {getPredicamentValue({
            label:
              translations.patient.patientInformation.supplementaryInsurance,
            translations,
            value: payers.supplementary_insurance_state,
          })}
        </BodyWithBreak>
      )}
      {payers.supplementary_insurance && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="supplementary_insurance">
          {payers.supplementary_insurance}
        </BodyWithBreak>
      )}
      {payers.supplementary_payment_state && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="supplementary_payment_state"
        >
          {getPredicamentValue({
            label: translations.patient.patientInformation.supplementaryPayment,
            translations,
            value: payers.supplementary_payment_state,
          })}
        </BodyWithBreak>
      )}
      {payers.supplementary_payment && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="supplementary_payment">
          {payers.supplementary_payment}
        </BodyWithBreak>
      )}
      {payers.free_from_supplementary_payment_state !==
        PREDICAMENT_STATE_NOT_APPLICABLE && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="free_from_supplementary_payment_state"
        >
          {getPredicamentValue({
            label:
              translations.patient.patientInformation
                .freeFromSupplementaryPayment,
            translations,
            value: payers.free_from_supplementary_payment_state,
          })}
        </BodyWithBreak>
      )}
    </div>
  );
}

function Grants({
  auction,
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  auction: Auction | undefined;
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  if (
    !patient?.profile?.financing ||
    auction?.search_type === SEARCH_TYPE_HOSPITAL ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  ) {
    return null;
  }

  const { financing } = patient.profile;

  const keys: (keyof Financing)[] = [
    "reimbursment_for_help_at_home",
    "reimbursment_secured",
    "preventative_care",
    "short_term_care",
    "relief_services",
    "private_payment_bool",
    "more_information",
    "social_help_recipient_bool",
  ];

  if (!keys.some((attr) => financing[attr] != null)) return null;

  return (
    <div>
      <Subheading bold>{translations.patient.grants}</Subheading>
      <StaticGrantStatusField
        getOntology={getOntology}
        label={translations.patient.reimbursmentForHelpAtHome}
        testId="reimbursment_for_help_at_home"
        translations={translations}
        value={financing.reimbursment_for_help_at_home}
      />
      <StaticGrantStatusField
        getOntology={getOntology}
        label={translations.patient.reimbursmentSecured}
        testId="reimbursment_secured"
        translations={translations}
        value={financing.reimbursment_secured}
      />
      <StaticGrantStatusField
        getOntology={getOntology}
        label={translations.patient.preventativeCare}
        testId="preventative_care"
        translations={translations}
        value={financing.preventative_care}
      />
      <StaticShortTermCareGrant
        getOntology={getOntology}
        testId="short_term_care"
        translations={translations}
        value={financing.short_term_care}
      />
      <StaticGrantStatusField
        getOntology={getOntology}
        label={translations.patient.socialHelpRecipient}
        testId="social_help_recipient_bool"
        translations={translations}
        value={financing.social_help_recipient_bool}
      />
      <BooleanField
        hasValue={financing.private_payment_bool}
        label={translations.patient.patientInformation.acceptsPrivatePayment}
      />
      {financing.relief_services && (
        <BodyWrapper>
          <Body
            margin={margin(0, -1.5, 0, 0)}
          >{`${translations.ontologies.reliefServices.key}${translations.general.colon} `}</Body>
          <OntologyValue
            getOntology={getOntology}
            type="reliefServices"
            value={financing.relief_services}
          />
        </BodyWrapper>
      )}
      {financing.more_information && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="more_information"
        >{`${translations.patient.patientInformation.grantsMoreInformation}: ${financing.more_information}`}</BodyWithBreak>
      )}
    </div>
  );
}

function Preferences({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  patient: Patient | undefined;
  translations: Translations;
}) {
  if (
    !patient?.profile?.preferences ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS ||
    patient.careseeker?.type === CARESEEKER_TYPE.PROVIDER_SEARCH
  )
    return null;

  const { preferences } = patient.profile;

  return (
    <div>
      <Subheading bold>
        {translations.patient.patientInformation.specialRequirements}
      </Subheading>
      {preferences.room_type != null && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="room_type">
          {getOntology({ key: preferences.room_type, type: "roomType" })}
        </BodyWithBreak>
      )}
      {preferences.barrier_free_room && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="barrier_free_room">
          {translations.patient.barrierFreeRoom}
        </BodyWithBreak>
      )}
      {preferences.accompanying_person && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="accompanying_person">
          {preferences.accompanying_person_description?.trim()
            ? `${translations.patient.accompanyingPerson}${
                translations.general.colon
              } ${preferences.accompanying_person_description.trim()}`
            : translations.patient.accompanyingPerson}
        </BodyWithBreak>
      )}
      {preferences.specialist_doctor && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="specialist_doctor">
          {translations.patient.specialistDoctor}
        </BodyWithBreak>
      )}
    </div>
  );
}

function MainDiagnosis({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (
    !patient.diagnosis?.main_diagnosis ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS
  )
    return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      isMultiline
      testId="main_diagnosis"
      text={patient.diagnosis.main_diagnosis}
      title={translations.patient.medicalDiagnosis.mainDiagnosis.label}
    />
  );
}

function SecondaryDiagnosis({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (
    !patient.diagnosis?.secondary_diagnosis ||
    patient.careseeker?.type === CARESEEKER_TYPE.ADMISSIONS
  )
    return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      isMultiline
      testId="secondary_diagnosis"
      text={patient.diagnosis.secondary_diagnosis}
      title={translations.patient.medicalDiagnosis.secondaryDiagnosis}
    />
  );
}

function MorbidityStatus({
  isActive,
  isFaxPrint,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  isActive: boolean | null | undefined;
  translations: Translations;
}) {
  if (!isActive) return null;

  return (
    <BodyWithBreak isFaxPrint={isFaxPrint} testId="multimorbidity">
      {translations.patient.medicalDiagnosis.multiMorbidPatient}
    </BodyWithBreak>
  );
}

function MedicalHistory({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.medical_history) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      isMultiline
      testId="medical_history"
      text={patient.diagnosis.medical_history}
      title={translations.patient.medicalHistory}
    />
  );
}

function AdditionalExaminationsNecessary({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.additional_examinations_necessary) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      isMultiline
      testId="additional_examinations_necessary"
      text={patient.diagnosis.additional_examinations_necessary}
      title={translations.patient.additionalExaminations}
    />
  );
}

function PalliativeCare({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.palliative) return null;

  const { palliative } = patient.care_needs;

  return (
    <EmptyablePrintCategory
      isEmpty={
        !palliative.description && !palliative.palliative_certificate_needed
      }
      isFaxPrint={isFaxPrint}
      testId="palliative"
      title={translations.patient.palliativeCare.label}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="palliative_description">
        {palliative.description}
      </BodyWithBreak>
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="palliative_certificate_needed"
      >
        {translations.patient.medicalDiagnosis.palliativeCertificateNeeded}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function DependencyDiagnosis({
  isFaxPrint,
  locale,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  locale: Locale;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.dependency_diagnosis) {
    return null;
  }

  const { dependency_diagnosis } = patient.diagnosis;

  return (
    <div>
      <Subheading bold>{translations.patient.autonomy}</Subheading>
      <StaticIndependenceScaleResult
        locale={locale}
        testId="barthel-assessment-result"
        title={translations.patient.medicalDiagnosis.barthelIndexPlaceholder}
        translations={translations}
        value={dependency_diagnosis?.barthel_assessment}
      />
      <StaticIndependenceScaleResult
        locale={locale}
        testId="singer-assessment-result"
        title={translations.patient.medicalDiagnosis.singerIndexPlaceholder}
        translations={translations}
        value={dependency_diagnosis?.singer_assessment}
      />
      <StaticIndependenceScaleResult
        locale={locale}
        testId="early-rehab-assessment-result"
        title={
          translations.patient.medicalDiagnosis
            .earlyRehabBarthelIndexPlaceholder
        }
        translations={translations}
        value={dependency_diagnosis?.early_rehab_assessment}
      />
      {dependency_diagnosis.requires_walking_aid !==
        PREDICAMENT_STATE_NOT_APPLICABLE && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="requires_walking_aid">
          {getPredicamentValue({
            label: translations.patient.needsHelpWalking,
            translations,
            value: dependency_diagnosis.requires_walking_aid,
          })}
        </BodyWithBreak>
      )}
      {dependency_diagnosis.requires_support_with_feeding !==
        PREDICAMENT_STATE_NOT_APPLICABLE && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="requires_support_with_feeding"
        >
          {getPredicamentValue({
            label: translations.patient.needsHelpEating,
            translations,
            value: dependency_diagnosis.requires_support_with_feeding,
          })}
        </BodyWithBreak>
      )}
      {dependency_diagnosis.agitated !== PREDICAMENT_STATE_NOT_APPLICABLE && (
        <>
          <BodyWithBreak isFaxPrint={isFaxPrint} testId="agitated">
            {getPredicamentValue({
              label: translations.patient.agitationOrientation,
              translations,
              value: dependency_diagnosis.agitated,
            })}
          </BodyWithBreak>
          {dependency_diagnosis.agitated_description && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="agitated_description"
            >
              {dependency_diagnosis.agitated_description}
            </BodyWithBreak>
          )}
        </>
      )}
      {dependency_diagnosis?.has_help_at_home !==
        PREDICAMENT_STATE_NOT_APPLICABLE && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="has_help_at_home">
          {getPredicamentValue({
            label: translations.patient.hasHelpAtHome,
            translations,
            value: dependency_diagnosis.has_help_at_home,
          })}
        </BodyWithBreak>
      )}
      {dependency_diagnosis?.returning_home_short_term !==
        PREDICAMENT_STATE_NOT_APPLICABLE && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="returning_home_short_term"
        >
          {getPredicamentValue({
            label: translations.patient.medicalDiagnosis.shortTerm,
            translations,
            value: dependency_diagnosis.returning_home_short_term,
          })}
        </BodyWithBreak>
      )}
    </div>
  );
}

function ChronicPains({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.chronic_pains) return null;

  return (
    <BasicCategory
      isFaxPrint={isFaxPrint}
      testId="chronic_pains"
      text={patient.diagnosis.chronic_pains}
      title={translations.patient.medicalDiagnosis.chronicPains}
    />
  );
}

function ImportantInformation({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient?.care_needs?.important_information) return null;

  return (
    <>
      <Subheading bold>
        {translations.patient.careNeeds.titleImportantInformationCare}
      </Subheading>
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="important_information">
        {patient.care_needs.important_information}
      </BodyWithBreak>
    </>
  );
}

function Dysphagia({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.dysphagia) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="dysphagia"
      text={patient.diagnosis.dysphagia}
      title={translations.patient.careNeeds.dysphagia.label}
    />
  );
}

function Allergies({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.allergies) return null;

  return (
    <BasicCategory
      isFaxPrint={isFaxPrint}
      isMultiline={false}
      testId="allergies"
      text={patient.diagnosis.allergies}
      title={translations.patient.medicalDiagnosis.allergies}
    />
  );
}

function IcdCodesForPrint({
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (
    !patient.diagnosis?.icd_code &&
    !patient.diagnosis?.additional_icd_codes
  ) {
    return null;
  }

  return (
    <StaticIcdCodes
      translations={translations}
      value={{
        additional_icd_codes: patient.diagnosis.additional_icd_codes,
        icd_code: patient.diagnosis.icd_code,
      }}
    />
  );
}

function CoronaVirusTesting({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  const { corona, corona_state } = patient?.diagnosis || {};

  if (
    !corona_state &&
    !corona?.test_result &&
    !corona?.description &&
    !corona?.vaccinated
  ) {
    return null;
  }

  const label = coronaSwitch(translations, { corona, corona_state });

  return (
    <>
      <Subheading bold>{translations.coronaTesting.auctionLabel}</Subheading>
      {label && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="corona_label">
          {translations.coronaTesting.results}
          {translations.general.colon} {label}
        </BodyWithBreak>
      )}
      {(corona_state === CORONA_NOT_TESTED ||
        corona_state === CORONA_TESTED) && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="corona_state">
          {corona?.description}
        </BodyWithBreak>
      )}
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="vaccinated">
        {typeof corona?.vaccinated === "string"
          ? `${translations.coronaTesting.hasBeenVaccinated}${
              corona?.vaccinated ? ` : ${corona?.vaccinated}` : ""
            }`
          : translations.coronaTesting.vaccinationUnknown}
      </BodyWithBreak>
    </>
  );
}

export function InfectionAndGerms({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis) return null;

  const { infection_and_germs, infection_and_germs_state } = patient.diagnosis;

  const [negativeState, negativeStateMessage] =
    isInfectionsAndGermsPredicamentNegative(
      { infection_and_germs_state },
      translations,
    );

  const remarks = infection_and_germs?.remarks;

  if (negativeState) {
    return (
      <>
        <Subheading bold>
          {translations.patient.medicalDiagnosis.infections}
        </Subheading>
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="infection_and_germs_state"
        >
          {negativeStateMessage}
        </BodyWithBreak>
        <StringField testId="infection_and_germs_remarks" value={remarks} />
      </>
    );
  }

  if (!infection_and_germs) {
    return null;
  }

  return (
    <div data-testid="infection_and_germs_state">
      <Subheading bold>
        {translations.patient.medicalDiagnosis.infections}
      </Subheading>
      <BooleanField
        hasValue={infection_and_germs.requires_isolation}
        label={
          translations.patient.medicalDiagnosis.infectionsRequiresIsolation
        }
      />
      <BooleanField
        hasValue={infection_and_germs.mrsa}
        label={translations.patient.medicalDiagnosis.infectionsMRSA}
      />
      <BooleanField
        hasValue={infection_and_germs.clostridien}
        label={translations.patient.medicalDiagnosis.infectionsClostridien}
      />
      <BooleanField
        hasValue={infection_and_germs.three_mrgn}
        label={translations.patient.medicalDiagnosis.infectionsThreeMRGN}
      />
      <BooleanField
        hasValue={infection_and_germs.four_mrgn}
        label={translations.patient.medicalDiagnosis.infectionsFourMRGN}
      />
      <BooleanField
        hasValue={infection_and_germs.vre}
        label={translations.patient.medicalDiagnosis.infectionsVRE}
      />
      <BooleanField
        hasValue={infection_and_germs.hepatitis_c}
        label={translations.patient.medicalDiagnosis.infectionsHepatitis}
      />
      <StringField testId="infection_and_germs_remarks" value={remarks} />
      <StringField
        testId="infection_and_germs_other"
        value={infection_and_germs.other}
      />
    </div>
  );
}

function Addictions({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.addictions) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="addictions"
      text={patient.diagnosis.addictions}
      title={translations.patient.medicalDiagnosis.addictions}
    />
  );
}

function PatientOrientation({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="mental_health"
      text={getOntology({
        key: patient.diagnosis.mental_health.orientation,
        type: ONTOLOGIES.ORIENTATION,
      })}
      title={translations.ontologies.orientation.key}
    />
  );
}

function Vigilance({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BodyWrapper data-testid="vigilance">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.ontologies.vigilance.key}${translations.general.colon} `}</Body>
      <OntologyValue
        getOntology={getOntology}
        type="vigilance"
        value={patient.diagnosis.mental_health.vigilance}
      />
    </BodyWrapper>
  );
}

function Consciousness({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BodyWrapper data-testid="consciousness">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.ontologies.consciousness.key}${translations.general.colon} `}</Body>
      <OntologyValue
        getOntology={getOntology}
        type="consciousness"
        value={patient.diagnosis.mental_health.consciousness}
      />
    </BodyWrapper>
  );
}

function ConsciousnessDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.diagnosis.mental_health.consciousness_description}
      isFaxPrint={isFaxPrint}
      testId="consciousness_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="consciousness_description">
        {patient.diagnosis.mental_health.consciousness_description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function ContactBehaviour({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BodyWrapper data-testid="contact_behavior">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.ontologies.contactBehaviour.key}${translations.general.colon} `}</Body>
      <OntologyValue
        getOntology={getOntology}
        type="contactBehaviour"
        value={
          patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
            ?.contact_behavior
        }
      />
    </BodyWrapper>
  );
}

function ContactBehaviourDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={
        !patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
          ?.contact_behavior_description
      }
      isFaxPrint={isFaxPrint}
      testId="contact_behavior_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="contact_behavior_description"
      >
        {
          patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
            ?.contact_behavior_description
        }
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function OrientationTime({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BodyWrapper data-testid="orientation_time">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.ontologies.orientation.keyTime}${translations.general.colon} `}</Body>
      <OntologyValue
        getOntology={getOntology}
        type="orientation"
        value={patient.diagnosis.mental_health.orientation_time}
      />
    </BodyWrapper>
  );
}

function OrientationTimeDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.diagnosis.mental_health.orientation_time_description}
      isFaxPrint={isFaxPrint}
      testId="orientation_time_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="orientation_time_description"
      >
        {patient.diagnosis.mental_health.orientation_time_description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function OrientationPlace({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BodyWrapper data-testid="orientation_place">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.ontologies.orientation.keyLocation}${translations.general.colon} `}</Body>
      <OntologyValue
        getOntology={getOntology}
        type="orientation"
        value={patient.diagnosis.mental_health.orientation_place}
      />
    </BodyWrapper>
  );
}

function OrientationPlaceDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.diagnosis.mental_health.orientation_place_description}
      isFaxPrint={isFaxPrint}
      testId="orientation_place_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="orientation_place_description"
      >
        {patient.diagnosis.mental_health.orientation_place_description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function OrientationSituation({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BodyWrapper data-testid="orientation_situation">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.ontologies.orientation.keySituation}${translations.general.colon} `}</Body>
      <OntologyValue
        getOntology={getOntology}
        type="orientation"
        value={patient.diagnosis.mental_health.orientation_situation}
      />
    </BodyWrapper>
  );
}

function OrientationSituationDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={
        !patient.diagnosis.mental_health.orientation_situation_description
      }
      isFaxPrint={isFaxPrint}
      testId="orientation_situation_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="orientation_situation_description"
      >
        {patient.diagnosis.mental_health.orientation_situation_description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function OrientationPerson({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BodyWrapper data-testid="orientation_person">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.ontologies.orientation.keyPerson}${translations.general.colon} `}</Body>
      <OntologyValue
        getOntology={getOntology}
        type="orientation"
        value={patient.diagnosis.mental_health.orientation_person}
      />
    </BodyWrapper>
  );
}

function OrientationPersonDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.diagnosis.mental_health.orientation_person_description}
      isFaxPrint={isFaxPrint}
      testId="orientation_person_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="orientation_person_description"
      >
        {patient.diagnosis.mental_health.orientation_person_description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function SelfHarm({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health?.self_harm?.state) return null;
  const selfHarm = patient.diagnosis.mental_health.self_harm;

  return (
    <>
      <BodyWrapper data-testid="self_harm">
        <Body
          margin={margin(0, -1.5, 0, 0)}
          style={{ fontWeight: FONT_WEIGHT_BOLD }}
        >{`${translations.ontologies.selfEndangerment.keySelfEndangerment}${translations.general.colon} `}</Body>
        <OntologyValue
          getOntology={getOntology}
          type="selfEndangerment"
          value={selfHarm.state}
        />
      </BodyWrapper>
      {selfHarm.state === PREDICAMENT_STATE_YES && (
        <>
          <BooleanField
            hasValue={selfHarm.cutting}
            label={
              translations.ontologies.selfEndangerment.subValues
                .cuttingScratching
            }
          />
          <BooleanField
            hasValue={selfHarm.scratching}
            label={
              translations.ontologies.selfEndangerment.subValues.scratching
            }
          />
          <BooleanField
            hasValue={selfHarm.hitting_surfaces}
            label={
              translations.ontologies.selfEndangerment.subValues.hittingSurfaces
            }
          />
          <BooleanField
            hasValue={selfHarm.hitting_self}
            label={
              translations.ontologies.selfEndangerment.subValues.hittingSelf
            }
          />
          <BooleanField
            hasValue={selfHarm.biting}
            label={translations.ontologies.selfEndangerment.subValues.biting}
          />
          <BooleanField
            hasValue={selfHarm.burning}
            label={translations.ontologies.selfEndangerment.subValues.burning}
          />
        </>
      )}
    </>
  );
}

function SelfHarmDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.diagnosis.mental_health.self_harm?.description}
      isFaxPrint={isFaxPrint}
      testId="self_harm_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="self_harm_description">
        {patient.diagnosis.mental_health.self_harm?.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function Suicidality({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health?.suicidality?.state) return null;
  const suicidality = patient.diagnosis.mental_health.suicidality;

  return (
    <>
      <BodyWrapper data-testid="suicidality">
        <Body
          margin={margin(0, -1.5, 0, 0)}
          style={{ fontWeight: FONT_WEIGHT_BOLD }}
        >{`${translations.ontologies.selfEndangerment.keySuicidalTendencies}${translations.general.colon} `}</Body>
        <OntologyValue
          getOntology={getOntology}
          type="selfEndangerment"
          value={suicidality.state}
        />
      </BodyWrapper>
      {suicidality.state === PREDICAMENT_STATE_YES && (
        <>
          <BooleanField
            hasValue={suicidality.passive_thoughts}
            label={
              translations.ontologies.selfEndangerment.subValues.passiveThoughts
            }
          />
          <BooleanField
            hasValue={suicidality.active_thoughts}
            label={
              translations.ontologies.selfEndangerment.subValues.activeThoughts
            }
          />
          <BooleanField
            hasValue={suicidality.concrete_plans}
            label={
              translations.ontologies.selfEndangerment.subValues.specificPlans
            }
          />
          <BooleanField
            hasValue={suicidality.preparatory_actions}
            label={
              translations.ontologies.selfEndangerment.subValues
                .preparatoryActions
            }
          />
          <BooleanField
            hasValue={suicidality.previous_attempts}
            label={
              translations.ontologies.selfEndangerment.subValues
                .previousAttempts
            }
          />
        </>
      )}
    </>
  );
}

function SuicidalityDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.diagnosis.mental_health.suicidality?.description}
      isFaxPrint={isFaxPrint}
      testId="suicidality_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="suicidality_description">
        {patient.diagnosis.mental_health.suicidality?.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function Endangerment({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health?.endangerment_of_others?.state)
    return null;
  const endangermentOfOthers =
    patient.diagnosis.mental_health.endangerment_of_others;

  return (
    <>
      <BodyWrapper data-testid="endangerment_of_others">
        <Body
          margin={margin(0, -1.5, 0, 0)}
          style={{ fontWeight: FONT_WEIGHT_BOLD }}
        >{`${translations.ontologies.endangermentOfOthers.key}${translations.general.colon} `}</Body>
        <OntologyValue
          getOntology={getOntology}
          type="endangermentOfOthers"
          value={endangermentOfOthers.state}
        />
      </BodyWrapper>
      {endangermentOfOthers.state === PREDICAMENT_STATE_YES && (
        <>
          <BooleanField
            hasValue={endangermentOfOthers.aggressive_impulses}
            label={
              translations.ontologies.endangermentOfOthers.subValues
                .aggressiveImpulses
            }
          />
          <BooleanField
            hasValue={endangermentOfOthers.verbal_aggression}
            label={
              translations.ontologies.endangermentOfOthers.subValues
                .verbalAggression
            }
          />
          <BooleanField
            hasValue={endangermentOfOthers.physical_aggression}
            label={
              translations.ontologies.endangermentOfOthers.subValues
                .physicalAggression
            }
          />
          <BooleanField
            hasValue={endangermentOfOthers.concrete_threats}
            label={
              translations.ontologies.endangermentOfOthers.subValues
                .specificThreats
            }
          />
          <BooleanField
            hasValue={endangermentOfOthers.lack_of_impulse_control}
            label={
              translations.ontologies.endangermentOfOthers.subValues
                .lackOfImpulseControl
            }
          />
        </>
      )}
    </>
  );
}

function EndangermentDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={
        !patient.diagnosis.mental_health.endangerment_of_others?.description
      }
      isFaxPrint={isFaxPrint}
      testId="endangerment_of_others_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="endangerment_of_others_description"
      >
        {patient.diagnosis.mental_health.endangerment_of_others?.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function SubstanceUse({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health?.substance_use?.state) return null;
  const substanceUse = patient.diagnosis.mental_health.substance_use;

  return (
    <>
      <BodyWrapper data-testid="substance_use">
        <Body
          margin={margin(0, -1.5, 0, 0)}
          style={{ fontWeight: FONT_WEIGHT_BOLD }}
        >{`${translations.ontologies.substanceUse.key}${translations.general.colon} `}</Body>
        <OntologyValue
          getOntology={getOntology}
          type="substanceUse"
          value={substanceUse.state}
        />
      </BodyWrapper>
      {substanceUse.state === PREDICAMENT_STATE_YES && (
        <>
          <BooleanField
            hasValue={substanceUse.occasional_use}
            label={translations.ontologies.substanceUse.subValues.occasionalUse}
          />
          <BooleanField
            hasValue={substanceUse.harmful_use}
            label={translations.ontologies.substanceUse.subValues.harmfulUse}
          />
          <BooleanField
            hasValue={substanceUse.dependency}
            label={translations.ontologies.substanceUse.subValues.addiction}
          />
          {substanceUse.consumed_substances && (
            <StringField
              testId="substance_use_consumed_substances"
              value={`${translations.ontologies.substanceUse.subValues.substancesUsed}: ${substanceUse.consumed_substances}`}
            />
          )}
        </>
      )}
    </>
  );
}

function SubstanceUseDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.diagnosis.mental_health.substance_use?.description}
      isFaxPrint={isFaxPrint}
      testId="substance_use_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="substance_use_description">
        {patient.diagnosis.mental_health.substance_use?.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function DailyStructure({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BodyWrapper data-testid="daily_structure_participation">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.ontologies.dailyStructure.key}${translations.general.colon} `}</Body>
      <OntologyValue
        getOntology={getOntology}
        type="dailyStructure"
        value={
          patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
            ?.daily_structure_participation
        }
      />
    </BodyWrapper>
  );
}

function DailyStructureDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={
        !patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
          ?.daily_structure_participation_description
      }
      isFaxPrint={isFaxPrint}
      testId="daily_structure_participation_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="daily_structure_participation_description"
      >
        {
          patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
            ?.daily_structure_participation_description
        }
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function GafScore({
  patient,
  translations,
}: {
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;
  const value =
    patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
      ?.gaf_score;
  if (value == null) return null;

  return (
    <BodyWrapper data-testid="gaf_score">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.patient.gafScore.title}${translations.general.colon} `}</Body>
      <Body>{value}</Body>
    </BodyWrapper>
  );
}

function MotivationPatientStatic({
  getOntology,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <BodyWrapper data-testid="motivation">
      <Body
        margin={margin(0, -1.5, 0, 0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
      >{`${translations.ontologies.motivationPatient.key}${translations.general.colon} `}</Body>
      <OntologyValue
        getOntology={getOntology}
        type="motivationPatient"
        value={
          patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
            ?.motivation
        }
      />
    </BodyWrapper>
  );
}

function MotivationPatientDescriptionStatic({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={
        !patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
          ?.motivation_description
      }
      isFaxPrint={isFaxPrint}
      testId="motivation_description"
      title={translations.patient.additionalInformation}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="motivation_description">
        {
          patient.diagnosis.mental_health.brief_sociopsychiatric_assessment
            ?.motivation_description
        }
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function PatientCommunication({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health?.communication) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="communication"
      text={getOntology({
        key: patient.diagnosis.mental_health.communication,
        type: ONTOLOGIES.COMMUNICATION,
      })}
      title={translations.ontologies.communication.key}
    />
  );
}

function PatientMotivation({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health?.motivation) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="motivation"
      text={getOntology({
        key: patient.diagnosis.mental_health.motivation,
        type: "motivation",
      })}
      title={translations.ontologies.motivation.key}
    />
  );
}

function Dementia({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  getOntology: GetOntologyType;
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  const mentalHealth = patient.diagnosis?.mental_health;
  if (!mentalHealth) return null;

  const ontologyValue = mentalHealth.dementia;
  const description = mentalHealth.dementia_description;

  if (!ontologyValue && !description) return null;

  if (ontologyValue) {
    return (
      <div
        style={{
          alignItems: "baseline",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Subheading bold>{translations.ontologies.dementia.key}</Subheading>
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="dementia">
          {getOntology({
            key: ontologyValue,
            type: ONTOLOGIES.DEMENTIA,
          })}
        </BodyWithBreak>
        {description != null && description !== "" && (
          <BodyWithBreak isFaxPrint={isFaxPrint} testId="dementia_description">
            {description}
          </BodyWithBreak>
        )}
      </div>
    );
  }

  // Non-psych: legacy display — show description under the "Demenz" label.
  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      isMultiline
      testId="dementia"
      text={description ?? ""}
      title={translations.patient.dementia}
    />
  );
}

function RunAway({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health?.run_away_description) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="run_away_description"
      text={patient.diagnosis.mental_health.run_away_description}
      title={translations.patient.closedAreaNeeded}
    />
  );
}

function ProtectedAreaNeeded({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health?.protected_area_needed) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="protected_area_needed"
      text={patient.diagnosis.mental_health.protected_area_needed}
      title={translations.patient.mentalState.protectedAreaNeeded.label}
    />
  );
}

function Nightwatch({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health?.night_watch_description) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="night_watch_description"
      text={patient.diagnosis.mental_health.night_watch_description}
      title={translations.patient.mentalState.nightwatch.label}
    />
  );
}

function FacilityCharacteristics({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (
    !patient.diagnosis?.mental_health?.closed_station &&
    !patient.diagnosis?.mental_health?.fixation
  ) {
    return null;
  }

  const { closed_station, fixation } = patient.diagnosis.mental_health;

  return (
    <div>
      <Subheading bold>
        {translations.patient.mentalState.facilityCharacteristics}
      </Subheading>
      {closed_station && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="closed_station">
          {translations.patient.mentalState.closedStationRequired}
        </BodyWithBreak>
      )}
      {fixation && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="fixation">
          {translations.patient.mentalState.fixationRequired}
        </BodyWithBreak>
      )}
    </div>
  );
}

function MentalIssues({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.mental_health) return null;

  const { mental_health } = patient.diagnosis;

  return (
    <div>
      <Subheading bold>
        {translations.patient.mentalState.mentalIssues}
      </Subheading>
      {mental_health.depression && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="depression">
          {translations.patient.mentalState.depression}
        </BodyWithBreak>
      )}
      {mental_health.anxiety && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="anxiety">
          {translations.patient.mentalState.anxiety}
        </BodyWithBreak>
      )}
      {mental_health.aggressivity && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="aggressivity">
          {translations.patient.mentalState.aggressivity}
        </BodyWithBreak>
      )}
      {mental_health.dangerous && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="dangerous">
          {translations.patient.mentalState.dangerous}
        </BodyWithBreak>
      )}
      {mental_health.mental_issues && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="mental_issues"
        >{`${translations.patient.mentalState.mentalIssuesOther}: ${mental_health.mental_issues}`}</BodyWithBreak>
      )}
    </div>
  );
}

function PatientMobility({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.physical_disabilities?.mobility) return null;

  return (
    <div>
      <Subheading bold>{translations.ontologies.mobility.key}</Subheading>
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="mobility">
        {getOntology({
          key: patient.diagnosis.physical_disabilities.mobility,
          type: ONTOLOGIES.MOBILITY,
        })}
      </BodyWithBreak>
      {patient.diagnosis.physical_disabilities
        .mobility_additional_information && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="physical_disabilities">
          {
            patient.diagnosis.physical_disabilities
              .mobility_additional_information
          }
        </BodyWithBreak>
      )}
    </div>
  );
}

function VisualImpairment({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.physical_disabilities?.visual_impairment) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="visual_impairment"
      text={patient.diagnosis.physical_disabilities.visual_impairment}
      title={translations.patient.mobility.visual_impairment.label}
    />
  );
}

function AuditiveImpairment({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.physical_disabilities?.auditive_impairment) {
    return null;
  }

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="auditive_impairment"
      text={patient.diagnosis.physical_disabilities.auditive_impairment}
      title={translations.patient.mobility.auditive_impairment.label}
    />
  );
}

function AssistiveEquipment({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.physical_disabilities) return null;

  const {
    assistive_equipment,
    existing_medical_supplies,
    prescribed_medical_supplies,
    required_medical_supplies,
  } = patient.diagnosis.physical_disabilities;

  const exists =
    !!assistive_equipment ||
    !!required_medical_supplies ||
    !!prescribed_medical_supplies ||
    !!existing_medical_supplies;
  const newValues =
    !!required_medical_supplies ||
    !!prescribed_medical_supplies ||
    !!existing_medical_supplies;
  return (
    <>
      {exists && (
        <Subheading bold>
          {translations.patient.mobility.assistiveEquipment}
        </Subheading>
      )}
      {assistive_equipment && !newValues && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="assistive_equipment"
        >{`${assistive_equipment}`}</BodyWithBreak>
      )}
      {existing_medical_supplies && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="existing_medical_supplies"
        >{`${translations.patient.medicalSupplies.existingMedicalSupplies}: ${existing_medical_supplies}`}</BodyWithBreak>
      )}
      {prescribed_medical_supplies && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="prescribed_medical_supplies"
        >{`${translations.patient.medicalSupplies.prescribedMedicalSupplies}: ${prescribed_medical_supplies}`}</BodyWithBreak>
      )}
      {required_medical_supplies && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="required_medical_supplies"
        >{`${translations.patient.medicalSupplies.neededMedicalSupplies}: ${required_medical_supplies}`}</BodyWithBreak>
      )}
    </>
  );
}

function Orthosis({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.physical_disabilities?.orthese) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="orthese"
      text={patient.diagnosis.physical_disabilities.orthese}
      title={translations.patient.mobility.orthese.label}
    />
  );
}

function Prothesis({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.physical_disabilities?.prothese) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="prothese"
      text={patient.diagnosis.physical_disabilities.prothese}
      title={translations.patient.mobility.prothese.label}
    />
  );
}

function HospitalStay({
  isFaxPrint,
  isForClinic,
  locale,
  patient,
  searchType,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  isForClinic?: boolean;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  patient: Patient;
  searchType: SearchType;
  translations: Translations;
}) {
  const isReha = isRehabSearch(searchType);

  const hasCommonFields =
    !!patient.diagnosis?.hospital_stay?.doctor_in_charge_in_hospital_phone ||
    !!patient.station_full?.name;

  const hasRehaFields =
    isReha &&
    (!!patient.diagnosis?.hospital_stay?.operation_date ||
      !!patient.diagnosis?.hospital_stay?.operation_description ||
      !!patient.diagnosis?.hospital_stay?.geriatric_early_stage ||
      !!patient.diagnosis?.hospital_stay?.doctor_in_charge_in_hospital);

  const notEmpty = hasCommonFields || hasRehaFields;

  const showHospitalStay = isForClinic
    ? isReha || notEmpty
    : isReha && notEmpty;

  if (!showHospitalStay) return null;

  return (
    <div>
      <Subheading bold>
        {translations.patient.medicalDiagnosis.hospitalStay}
      </Subheading>
      {isForClinic && (
        <>
          {patient.diagnosis?.hospital_stay?.doctor_in_charge_in_hospital && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="doctor_in_charge_in_hospital"
            >
              {`${translations.patient.medicalDiagnosis.doctorInChargeInHospital}: ${patient.diagnosis.hospital_stay.doctor_in_charge_in_hospital}`}
            </BodyWithBreak>
          )}
          {patient.station_full?.name && (
            <BodyWithBreak isFaxPrint={isFaxPrint} testId="station_full_name">
              {`${translations.patient.station}: ${patient.station_full.name}`}
            </BodyWithBreak>
          )}
          {patient.diagnosis?.hospital_stay
            ?.doctor_in_charge_in_hospital_phone && (
            <BodyWithBreak
              isFaxPrint={isFaxPrint}
              testId="doctor_in_charge_in_hospital_phone"
            >
              {`${translations.patient.stationPhoneNumber}: ${patient.diagnosis.hospital_stay.doctor_in_charge_in_hospital_phone}`}
            </BodyWithBreak>
          )}
        </>
      )}
      {patient?.diagnosis?.hospital_stay?.operation_date && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="operation_date">
          {`${
            translations.patient.medicalDiagnosis.operationDate
          }: ${formatUnixDate(
            patient.diagnosis.hospital_stay.operation_date,
            locale,
          )}`}
        </BodyWithBreak>
      )}
      {patient?.diagnosis?.hospital_stay?.operation_description && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="operation_description">
          {`${translations.patient.medicalDiagnosis.operationDescription}: ${patient.diagnosis.hospital_stay.operation_description}`}
        </BodyWithBreak>
      )}
      {patient?.diagnosis?.hospital_stay?.geriatric_early_stage && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="geriatric_early_stage">
          {translations.patient.medicalDiagnosis.conductedEarlyRehab}:
          {patient.diagnosis.hospital_stay.geriatric_early_stage}
        </BodyWithBreak>
      )}
    </div>
  );
}

function RehabilitationPotential({
  getOntology,
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.rehabilitation_potential) return null;

  const { rehabilitation_potential } = patient.diagnosis;

  return (
    <div>
      <Subheading bold>
        {translations.patient.medicalDiagnosis.rehabilitationPotential}
      </Subheading>
      {rehabilitation_potential.evaluation != null && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="evaluation">
          {getOntology({
            key: rehabilitation_potential.evaluation,
            type: "rehabilitationPotential",
          })}
        </BodyWithBreak>
      )}
      {rehabilitation_potential.description && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="rehabilitation_potential_description"
        >
          {rehabilitation_potential.description}
        </BodyWithBreak>
      )}
    </div>
  );
}

function LimitedCapacity({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (
    !patient?.diagnosis?.limited_capacity ||
    !activableInputCollectionIsNotEmpty(patient.diagnosis.limited_capacity)
  ) {
    return null;
  }

  const { limited_capacity } = patient.diagnosis;

  return (
    <div>
      <Subheading bold>
        {translations.patient.medicalDiagnosis.limitations}
      </Subheading>
      {limited_capacity.limited_psychological_capacity && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="limited_psychological_capacity"
        >
          {`${translations.patient.medicalDiagnosis.limitedPsychologicalCapacity}: ${limited_capacity.limited_psychological_capacity}`}
        </BodyWithBreak>
      )}
      {limited_capacity.limited_cognitive_capacity && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="limited_cognitive_capacity"
        >
          {`${translations.patient.medicalDiagnosis.limitedCognitiveCapacity}: ${limited_capacity.limited_cognitive_capacity}`}
        </BodyWithBreak>
      )}
      {limited_capacity.limited_social_behaviour && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="limited_social_behaviour"
        >
          {`${translations.patient.medicalDiagnosis.limitedSocialBehaviour}: ${limited_capacity.limited_social_behaviour}`}
        </BodyWithBreak>
      )}
      {limited_capacity.limited_physical_capacity && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="limited_physical_capacity"
        >
          {`${translations.patient.medicalDiagnosis.limitedPsychicalCapacity}: ${limited_capacity.limited_physical_capacity}`}
        </BodyWithBreak>
      )}
      {limited_capacity.other && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="limited_capacity_other">
          {`${translations.patient.medicalDiagnosis.limitedCapacityOther}: ${limited_capacity.other}`}
        </BodyWithBreak>
      )}
    </div>
  );
}

function RehabilitationGoals({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.diagnosis?.rehabilitation_goals) return null;

  return (
    <BasicCategory
      isColumn
      isFaxPrint={isFaxPrint}
      testId="rehabilitation_goals"
      text={patient.diagnosis.rehabilitation_goals}
      title={translations.patient.medicalDiagnosis.rehabilitationGoals}
    />
  );
}

function Nutrition({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.nutrition) return null;

  const { nutrition } = patient.care_needs;

  return (
    <EmptyablePrintCategory
      isEmpty={
        nutrition.parenteral_nutrition == null &&
        nutrition.enteral_nutrition == null
      }
      isFaxPrint={isFaxPrint}
      testId="nutrition"
      title={translations.patient.careNeeds.nutrition}
      translations={translations}
    >
      {nutrition.parenteral_nutrition && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="parenteral_nutrition"
        >{`${translations.patient.careNeeds.parenteralNutrition.label}: ${nutrition.parenteral_nutrition}`}</BodyWithBreak>
      )}
      {nutrition.enteral_nutrition && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="enteral_nutrition"
        >{`${translations.patient.careNeeds.enteralNutrition.label}: ${nutrition.enteral_nutrition}`}</BodyWithBreak>
      )}
    </EmptyablePrintCategory>
  );
}

function Incontinence({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.incontinence) return null;

  const { incontinence } = patient.care_needs;

  return (
    <EmptyablePrintCategory
      isEmpty={
        !incontinence.urinary_incontinence && !incontinence.fecal_incontinence
      }
      isFaxPrint={isFaxPrint}
      testId="incontinence"
      title={translations.patient.careNeeds.incontinence}
      translations={translations}
    >
      {incontinence.urinary_incontinence && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="urinary_incontinence"
        >{`${translations.patient.careNeeds.urinaryIncontinence}: ${incontinence.urinary_incontinence}`}</BodyWithBreak>
      )}
      {incontinence.fecal_incontinence && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="fecal_incontinence"
        >{`${translations.patient.careNeeds.fecalIncontinence}: ${incontinence.fecal_incontinence}`}</BodyWithBreak>
      )}
    </EmptyablePrintCategory>
  );
}

function Breathing({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.breathing) return null;

  const { breathing } = patient.care_needs;

  return (
    <EmptyablePrintCategory
      isEmpty={
        !breathing.tracheostomy &&
        !breathing.specialised_intensive_nurse &&
        !breathing.oxygen_therapy
      }
      isFaxPrint={isFaxPrint}
      testId="breathing"
      title={translations.patient.careNeeds.breathing}
      translations={translations}
    >
      {breathing.tracheostomy && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="tracheostomy"
        >{`${translations.patient.careNeeds.tracheostomy.label}: ${breathing.tracheostomy}`}</BodyWithBreak>
      )}
      {breathing.specialised_intensive_nurse && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="specialised_intensive_nurse"
        >
          {translations.ontologies.service.values.specialisedIntensiveNurse}
        </BodyWithBreak>
      )}
      {breathing.oxygen_therapy && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="oxygen_therapy"
        >{`${translations.patient.careNeeds.oxygenTherapy.label}: ${breathing.oxygen_therapy}`}</BodyWithBreak>
      )}
    </EmptyablePrintCategory>
  );
}

function Dialysis({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.dialysis) return null;

  const { dialysis } = patient.care_needs;

  return (
    <EmptyablePrintCategory
      isEmpty={!dialysis.hemodialysis && !dialysis.peritoneal_dialysis}
      isFaxPrint={isFaxPrint}
      testId="dialysis"
      title={translations.patient.careNeeds.dialysis}
      translations={translations}
    >
      {dialysis.hemodialysis != null && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="hemodialysis"
        >{`${translations.patient.careNeeds.hemodialysis.label}: ${dialysis.hemodialysis}`}</BodyWithBreak>
      )}
      {dialysis.peritoneal_dialysis != null && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="peritoneal_dialysis"
        >{`${translations.patient.careNeeds.peritonealDialysis.label}: ${dialysis.peritoneal_dialysis}`}</BodyWithBreak>
      )}
    </EmptyablePrintCategory>
  );
}

function Wounds({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.wound_care) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.wound_care.description}
      isFaxPrint={isFaxPrint}
      testId="wound_care_description"
      title={translations.patient.careNeeds.woundCare}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="wound_care_description">
        {patient.care_needs.wound_care.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function Support({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.support) return null;

  const { support } = patient.care_needs;

  return (
    <EmptyablePrintCategory
      isEmpty={!support.eating && !support.body_care && !support.dressing}
      isFaxPrint={isFaxPrint}
      testId="support"
      title={translations.patient.careNeeds.needSupport}
      translations={translations}
    >
      {support.eating && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="eating">
          {translations.patient.careNeeds.withEating}
        </BodyWithBreak>
      )}
      {support.body_care && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="body_care">
          {translations.patient.careNeeds.withBodyCare}
        </BodyWithBreak>
      )}
      {support.dressing && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="dressing">
          {translations.patient.careNeeds.withDressing}
        </BodyWithBreak>
      )}
    </EmptyablePrintCategory>
  );
}

function PsychiatricCare({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.psychiatric_care) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.psychiatric_care.description}
      isFaxPrint={isFaxPrint}
      testId="psychiatric_care"
      title={translations.patient.careNeeds.psychiatric_care.label}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="psychiatric_care">
        {patient.care_needs.psychiatric_care.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function NeedForAssistanceDescription({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.need_for_assistance_description) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.need_for_assistance_description}
      isFaxPrint={isFaxPrint}
      testId="need_for_assistance_description"
      title={translations.patient.careNeeds.stepperTitlePsychiatricCareNeeds}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="need_for_assistance_description"
      >
        {patient.care_needs.need_for_assistance_description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function IntegrationAssistance({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.integration_assistance) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.integration_assistance.description}
      isFaxPrint={isFaxPrint}
      testId="integration_assistance"
      title={translations.patient.careNeeds.integrationAssistance.label}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="integration_assistance">
        {patient.care_needs.integration_assistance.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function Medication({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.medication}
      isFaxPrint={isFaxPrint}
      testId="medication"
      title={translations.patient.careNeeds.medication}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="medication">
        {patient.care_needs.medication?.description}
      </BodyWithBreak>
      {patient.care_needs.medication?.blood_thinner && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="blood_thinner">
          {`${translations.patient.takesBloodThinner}${translations.general.colon} ${patient.care_needs.medication.blood_thinner}`}
        </BodyWithBreak>
      )}
    </EmptyablePrintCategory>
  );
}

function Compression({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.compression) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.compression.description}
      isFaxPrint={isFaxPrint}
      testId="compression"
      title={translations.patient.careNeeds.compression}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="compression">
        {patient.care_needs.compression.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function Injection({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.injection) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.injection.description}
      isFaxPrint={isFaxPrint}
      testId="injection"
      title={translations.patient.careNeeds.injection}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="injection">
        {patient.care_needs.injection.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function AccessDrainages({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  const { access_drainages } = patient.care_needs || {
    access_drainages: undefined,
  };
  if (!access_drainages) return null;

  const keys: (keyof AccessDrainagesType)[] = [
    "tracheal_cannula",
    "gastric_tube",
    "central_venous_catheter",
    "peripheral_venous_catheter",
    "suprapubic_catheter",
    "permanent_catheter",
    "drainage",
    "intravenous_injection",
    "other_access_drainages",
  ];

  return (
    <EmptyablePrintCategory
      isEmpty={!keys.find((attr) => access_drainages[attr] != null)}
      isFaxPrint={isFaxPrint}
      testId="access_drainages"
      title={translations.patient.careNeeds.accessDrainages.accessAndDrainages}
      translations={translations}
    >
      <EmptiablePrintField
        isFaxPrint={isFaxPrint}
        prefix={translations.patient.careNeeds.accessDrainages.trachealCannula}
        testId="tracheal_cannula"
        value={access_drainages.tracheal_cannula}
      />
      <EmptiablePrintField
        isFaxPrint={isFaxPrint}
        prefix={translations.patient.careNeeds.accessDrainages.gastricTube}
        testId="gastric_tube"
        value={access_drainages.gastric_tube}
      />
      <EmptiablePrintField
        isFaxPrint={isFaxPrint}
        prefix={
          translations.patient.careNeeds.accessDrainages.centralVenousCatheter
        }
        testId="central_venous_catheter"
        value={access_drainages.central_venous_catheter}
      />
      <EmptiablePrintField
        isFaxPrint={isFaxPrint}
        prefix={
          translations.patient.careNeeds.accessDrainages
            .peripheralVenousCatheter
        }
        testId="peripheral_venous_catheter"
        value={access_drainages.peripheral_venous_catheter}
      />
      <EmptiablePrintField
        isFaxPrint={isFaxPrint}
        prefix={
          translations.patient.careNeeds.accessDrainages.suprapubicCatheter
        }
        testId="suprapubic_catheter"
        value={access_drainages.suprapubic_catheter}
      />
      <EmptiablePrintField
        isFaxPrint={isFaxPrint}
        prefix={
          translations.patient.careNeeds.accessDrainages.permanentCatheter
        }
        testId="permanent_catheter"
        value={access_drainages.permanent_catheter}
      />
      <EmptiablePrintField
        isFaxPrint={isFaxPrint}
        prefix={translations.patient.careNeeds.accessDrainages.drainage}
        testId="drainage"
        value={access_drainages.drainage}
      />
      <EmptiablePrintField
        isFaxPrint={isFaxPrint}
        prefix={translations.patient.intravenousInjection}
        testId="intravenous_injection"
        value={access_drainages.intravenous_injection}
      />
      <EmptiablePrintField
        isFaxPrint={isFaxPrint}
        prefix={translations.patient.careNeeds.accessDrainages.other}
        testId="other_access_drainages"
        value={access_drainages.other_access_drainages}
      />
    </EmptyablePrintCategory>
  );
}

function BaseCare({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.base_care) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={
        !patient.care_needs.base_care.description &&
        !patient.care_needs.base_care.companionship_grant
      }
      isFaxPrint={isFaxPrint}
      testId="base_care"
      title={translations.patient.careNeeds.baseCare}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="base_care">
        {patient.care_needs.base_care.description}
      </BodyWithBreak>
      {patient.care_needs.base_care.companionship_grant && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="companionship_grant">
          {translations.patient.careNeeds.carePaidByHealthInsurance}
        </BodyWithBreak>
      )}
    </EmptyablePrintCategory>
  );
}

function MedicalCare({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs?.basic_medical_care?.description}
      isFaxPrint={isFaxPrint}
      testId="basic_medical_care_description"
      title={translations.patient.careNeeds.medicalCare}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="basic_medical_care_description"
      >
        {patient.care_needs?.basic_medical_care?.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function Prescription({
  auction,
  isFaxPrint,
  translations,
}: {
  auction: Auction;
  isFaxPrint: boolean | null | undefined;
  translations: Translations;
}) {
  return (
    <EmptyablePrintCategory
      isEmpty={!auction.medical_supplies?.prescription_issued}
      isFaxPrint={isFaxPrint}
      testId="prescription_issued"
      title={translations.assessments.medicalSupplies.prescriptionIssued}
      translations={translations}
    >
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="prescription_issued">
        {auction.medical_supplies?.prescription_issued_description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function Products({
  auction,
  getOntology,
  isFaxPrint,
  translations,
}: {
  auction: Auction;
  isFaxPrint: boolean | null | undefined;
  getOntology: GetOntologyType;
  translations: Translations;
}) {
  return (
    <>
      <Subheading>
        {translations.assessments.medicalSupplies.medicalSupplies}
      </Subheading>
      {auction.medical_supplies?.products?.map((product) => (
        <BodyWithBreak
          key={product.id}
          isFaxPrint={isFaxPrint}
          testId="medical_supplies_products"
        >
          {formatProductString(product, translations)}
        </BodyWithBreak>
      ))}
      {auction.medical_supplies?.additional_information && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="medical_supplies_additional_information"
        >
          <span style={{ fontWeight: FONT_WEIGHT_BOLD }}>
            {translations.patient.additionalInformation}
          </span>
          {translations.general.colon}{" "}
          {auction.medical_supplies?.additional_information}
        </BodyWithBreak>
      )}
      {auction.specializations && auction.specializations.length > 0 && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="home_care_specialization"
        >
          <span style={{ fontWeight: FONT_WEIGHT_BOLD }}>
            {translations.careproviderApp.notifications.specialization}
          </span>
          {translations.general.colon}{" "}
          {auction.specializations
            .map((s) => getOntology({ key: s, type: "specializations" }))
            .join(", ")}
        </BodyWithBreak>
      )}
    </>
  );
}

function DeliveryDetails({
  auction,
  isFaxPrint,
  translations,
}: {
  auction: Auction;
  isFaxPrint: boolean | null | undefined;
  translations: Translations;
}) {
  const { deliveryAddressPrefix, deliveryAddressString } = locationValue(
    auction,
    translations,
  );
  return (
    <EmptyablePrintCategory
      isEmpty={!auction.medical_supplies?.delivery_address_type}
      isFaxPrint={isFaxPrint}
      testId="delivery_address_type"
      title={translations.assessments.medicalSupplies.deliveryDetails}
      translations={translations}
    >
      {deliveryAddressString && (
        <BodyWithBreak
          isFaxPrint={isFaxPrint}
          testId="delivery_address_type"
        >{`${deliveryAddressPrefix}: ${deliveryAddressString}`}</BodyWithBreak>
      )}
    </EmptyablePrintCategory>
  );
}

function Companionship({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.companionship) return null;

  return (
    <div>
      <Subheading bold>
        {translations.patient.careNeeds.companionship}
      </Subheading>
      <BodyWithBreak isFaxPrint={isFaxPrint} testId="companionship">
        {patient.care_needs.companionship.description}
      </BodyWithBreak>
      {patient.care_needs.companionship.companionship_grant && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="companionship_grant">
          {translations.patient.careNeeds.carePaidByHealthInsurance}
        </BodyWithBreak>
      )}
    </div>
  );
}

function HelpAtHome({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.help_at_home) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.help_at_home.description}
      isFaxPrint={isFaxPrint}
      testId="care_needs.help_at_home_description"
      title={translations.patient.careNeeds.helpAtHome}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="care_needs.help_at_home_description"
      >
        {patient.care_needs.help_at_home.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function Remarks({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.remarks) return null;

  return (
    <div>
      <Subheading bold>{translations.patient.careNeeds.remarks}</Subheading>
      {patient.care_needs.remarks && (
        <BodyWithBreak isFaxPrint={isFaxPrint} testId="care_needs_remarks">
          {patient.care_needs.remarks}
        </BodyWithBreak>
      )}
    </div>
  );
}

function CareForChildrenAndTeenagers({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.care_for_children_and_teenagers) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.care_for_children_and_teenagers.description}
      isFaxPrint={isFaxPrint}
      testId="care_needs.care_for_children_and_teenagers_description"
      title={translations.patient.careNeeds.careForChildrenAndTeenagers.title}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="care_needs.care_for_children_and_teenagers_description"
      >
        {patient.care_needs.care_for_children_and_teenagers.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

function CareForYouth({
  isFaxPrint,
  patient,
  translations,
}: {
  isFaxPrint: boolean | null | undefined;
  patient: Patient;
  translations: Translations;
}) {
  if (!patient.care_needs?.care_for_youth) return null;

  return (
    <EmptyablePrintCategory
      isEmpty={!patient.care_needs.care_for_youth.description}
      isFaxPrint={isFaxPrint}
      testId="care_needs.care_for_youth_description"
      title={translations.patient.careNeeds.careForYouth.title}
      translations={translations}
    >
      <BodyWithBreak
        isFaxPrint={isFaxPrint}
        testId="care_needs.care_for_youth_description"
      >
        {patient.care_needs.care_for_youth.description}
      </BodyWithBreak>
    </EmptyablePrintCategory>
  );
}

const StaticPatientProfileContent = ({
  auction,
  auctionRequest,
  getOntology,
  isFaxPrint,
  isForClinic,
  isSealdOnly,
  locale,
  translations,
  withEncryptionAccess,
}: {
  auctionRequest: AuctionRequestType | undefined;
  auction: Auction;
  isFaxPrint: boolean | null | undefined;
  isForClinic: boolean;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  translations: Translations;
  isSealdOnly: boolean;
  withEncryptionAccess: boolean;
}) => {
  const { isAdmissionsRequest, isProviderSearchRequest } =
    getRequestOrigin(auctionRequest);

  if (isAdmissionsRequest) {
    return (
      // Hack to remove the padding from the parent container
      <div className="mx-[-24px]">
        <AdmissionsPatientCardPrint
          auction={auction}
          getOntology={getOntology}
          isSealdOnly={isSealdOnly}
          translations={translations}
          withEncryptionAccess={withEncryptionAccess}
        />
        <AdmissionsCareCardPrint
          auction={auction}
          getOntology={getOntology}
          locale={locale}
          translations={translations}
        />
        <AdmissionsMedicalInformationCardPrint
          auction={auction}
          getOntology={getOntology}
          isSealdOnly={isSealdOnly}
          translations={translations}
          withEncryptionAccess={withEncryptionAccess}
        />
        <AdmissionsOtherInformationCardPrint
          auction={auction}
          getOntology={getOntology}
          translations={translations}
        />
        <AdmissionsMainContactCardPrint
          auction={auction}
          getOntology={getOntology}
          isSealdOnly={isSealdOnly}
          translations={translations}
          withEncryptionAccess={withEncryptionAccess}
        />
      </div>
    );
  }

  if (isProviderSearchRequest) {
    return (
      <>
        <CareNeeds
          auction={auction}
          isFaxPrint={isFaxPrint}
          isForClinic={!!isForClinic}
          translations={translations}
        />
        <StaticTransportInfoCard
          auction={auction}
          getOntology={getOntology}
          translations={translations}
        />
      </>
    );
  }

  return (
    <>
      <Diagnosis
        auction={auction}
        getOntology={getOntology}
        isFaxPrint={isFaxPrint}
        isForClinic={!!isForClinic}
        locale={locale}
        patient={auction.patient}
        request={auctionRequest}
        translations={translations}
      />
      <MentalHealth
        auction={auction}
        getOntology={getOntology}
        isFaxPrint={isFaxPrint}
        isForClinic={!!isForClinic}
        translations={translations}
      />
      <PhysicalHealth
        auction={auction}
        getOntology={getOntology}
        isFaxPrint={isFaxPrint}
        isForClinic={!!isForClinic}
        translations={translations}
      />
      <MedicalSuppliesContainer
        auction={auction}
        getOntology={getOntology}
        isFaxPrint={isFaxPrint}
        isForClinic={!!isForClinic}
        translations={translations}
      />
      <CareNeeds
        auction={auction}
        isFaxPrint={isFaxPrint}
        isForClinic={!!isForClinic}
        translations={translations}
      />
      <StaticTransportInfoCard
        auction={auction}
        getOntology={getOntology}
        translations={translations}
      />
    </>
  );
};

export function StaticPatientProfile({
  auction,
  auctionRequest,
  currentLocale,
  getOntology,
  isFaxPrint,
  isForClinic,
  isSealdOnly,
  locale,
  translations,
  withEncryptionAccess,
}: {
  isSealdOnly: boolean;
  withEncryptionAccess: boolean;
  auction: Auction;
  auctionRequest?: AuctionRequestType;
  currentLocale: DateFnsLocale;
  isFaxPrint?: boolean;
  isForClinic?: boolean;
  getOntology: GetOntologyType;
  locale: TranslationsLocale;
  translations: Translations;
}) {
  const hideStreetAndHouseNumber = shouldHideStreetAndHouseNumber(
    auction.search_type,
    auction.solutions,
  );

  return (
    <div style={{ margin: margin(0, 2) }}>
      <GeneralInformationContainer
        auction={auction}
        currentLocale={currentLocale}
        getOntology={getOntology}
        hideStreetAndHouseNumber={hideStreetAndHouseNumber}
        isFaxPrint={isFaxPrint}
        isForClinic={!!isForClinic}
        locale={locale}
        patient={auction.patient}
        request={auctionRequest}
        translations={translations}
      />
      <StaticPatientProfileContent
        auction={auction}
        auctionRequest={auctionRequest}
        getOntology={getOntology}
        isFaxPrint={isFaxPrint}
        isForClinic={!!isForClinic}
        isSealdOnly={isSealdOnly}
        locale={locale}
        translations={translations}
        withEncryptionAccess={withEncryptionAccess}
      />
    </div>
  );
}
